/**
 * Webinars.tsx — v2 Webinar analytics
 *
 * Architecture:
 *  - Optins: urban_unity_webinar_optins (by assignedWebinarId) + main urban_unity_optins (date+UTM filter)
 *  - Apps/Calls/Cash: matched by email from main collections
 *  - Zoom: uploaded CSV per webinar
 *  - Paid = utm_source = "paid ads"
 *
 * Features:
 *  - Clickable funnel blocks → lead list modal → lead timeline drawer
 *  - In-funnel All/Compare toggle (organic vs paid side-by-side)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, ReferenceLine,
} from "recharts";
import { branding } from "../config/branding";
import { useTheme } from "../context/ThemeContext";
import * as XLSX from "xlsx";
import { WEBINAR_ON_WEBINAR_TARGETS, WEBINAR_SALES_TARGETS, WEBINAR_ZOOM_TARGETS, resolveWebinarTarget, type WebinarTargetMap } from "../config/webinar-targets";

// ─── Types ───────────────────────────────────────────────────────────────────

interface WebinarDef {
  id: string;
  name: string;
  date: string;
  funnel?: string;
  platform?: string;
  creator?: string;
  audienceType?: "warm" | "cold";
}

// Enhanced metrics types
interface SalesMetricsBlock {
  optins: number; apps: number; fullApps: number; partialApps: number;
  bookedCalls: number; takenCalls: number; noShows: number;
  cancelledCalls?: number; cancelledBySales: number; cancelledByLead: number; rescheduled: number;
  closed: number; deposits: number; showRate: number; closeRate: number; cash: number;
  cashFromOptins: number; cashFromApplications: number; cashFromBookedCalls: number; auv: number;
  cashPerCall: number; costPerBooking: number; appToBookingRate: number;
  qualifiedApps: number; qualifiedBooked: number; qualifiedToBookingRate: number; chargebackRate: number; roas: number;
}

interface OnWebinarMetrics {
  registrants: number; cpr: number; attended: number; showRate: number;
  avgDuration: number; bookingRate: number; applicationRate: number;
  pitchMinute: number | null; retentionAtPitch: number; attendeesAtPitch: number;
}

interface FilterOptions {
  sources: string[];
  mediums: string[];
  campaigns: string[];
  contents: string[];
  investmentLevels: string[];
}

interface LowTicketMetrics {
  purchasers: number; purchaseRate: number; liquidationPct: number; auv: number;
  purchaserWebinarShowRate: number; purchaserToBookingRate: number;
  purchaserToShowRate: number; purchaserToCloseRate: number; totalRevenue: number;
}

interface DialMetrics {
  totalDials: number; pickupRate: number; callsBooked: number;
}

interface EnhancedWebinarData {
  webinarId: string; webinarName: string; webinarDate: string;
  adSpend: number; hasZoomData: boolean;
  salesTotal: SalesMetricsBlock; salesOnWebby: SalesMetricsBlock; salesPostWebby: SalesMetricsBlock;
  onWebinar: OnWebinarMetrics; lowTicket: LowTicketMetrics; dials: DialMetrics;
  webinarStartTime: string | null; webinarEndTime: string | null;
  totalEocReports: number; totalEocsDue: number; totalEocsTaken: number;
  callShowRateByDay: { dayOffset: number; label: string; eocsDue: number; eocsTaken: number; showRate: number }[];
  addToCalendar: number; joinedWhatsApp: number;
}

interface WebinarFunnel {
  webinarId: string;
  webinarName: string;
  webinarDate: string;
  totalRegistrants: number;
  totalAttended: number;
  showRate: number;
  avgDuration: number;
  totalOptins: number;
  paidOptins: number;
  organicOptins: number;
  totalApps: number;
  fullApps: number;
  partialApps: number;
  paidApps: number;
  organicApps: number;
  totalCalls: number;
  paidCalls: number;
  organicCalls: number;
  totalShowed: number;
  paidShowed: number;
  organicShowed: number;
  totalWins: number;
  paidWins: number;
  organicWins: number;
  totalCash: number;
  paidCash: number;
  organicCash: number;
  optinToAppRate: number;
  appToCallRate: number;
  callShowRate: number;
  callToCloseRate: number;
  qualifiedApps: number;
  investmentBreakdown: Record<string, number>;
  hasZoomData: boolean;
  adSpend: number;
  roas: number;
  landingPageVisitors: number;
  optinRate: number;
}

interface RetentionPoint {
  minute: number;
  count: number;
  pct: number;
}

interface RetentionBySourcePoint {
  minute: number;
  [source: string]: number;
}

interface CashBySourceItem {
  source: string;
  cash: number;
  wins: number;
}
interface CreatorDistribution { total: { name: string; count: number }[]; onWebby: { name: string; count: number }[]; postWebby: { name: string; count: number }[]; }
interface CreatorDistributions { applications: { all: CreatorDistribution; full: CreatorDistribution; partial: CreatorDistribution }; bookedCalls: CreatorDistribution; }

interface FunnelStage {
  label: string;
  value: number;
  subLabel?: string;
  isCash?: boolean;
}

interface StageLead {
  email: string;
  name: string;
  source: string;
  outcome?: string;
  cash?: number;
  closedAt?: string;
  webinarOptinCount?: number;
}

interface RegTimingPoint {
  label: string;
  daysBeforeWebinar: number;
  registered: number;
  attended: number;
  showRate: number;
}

// Lead timeline types (mirrored from Leads.tsx)
interface TimelineOptin {
  date: string; source: string; medium: string; campaign: string; content: string; sheet: string;
}
interface TimelineApplication {
  date: string; source: string; medium: string; campaign: string; content: string; responseType: string;
}
interface TimelineCall {
  date: string; outcome: string; closer: string; setter: string; cash: number; offer: string;
  notes: string; fathomLink: string; platform: string; funnel: string;
}
interface TimelineWebinarOptin {
  date: string; webinarName: string; webinarId: string; source: string; medium: string; campaign: string;
}
interface TimelineMeetingBooking {
  bookedAt: string; meetingAt: string; closer: string;
}
interface LeadTimelineData {
  email: string; name: string; phone: string; country: string; leadType: string;
  profile: { hasBrand: string; currentMRR: string; ableToInvest: string };
  optins: TimelineOptin[];
  application: TimelineApplication | null;
  calls: TimelineCall[];
  webinarOptins: TimelineWebinarOptin[];
  meetingBookings: TimelineMeetingBooking[];
  totalRevenue: number;
  isClosed: boolean;
}

// Stage label → API key mapping
const STAGE_KEY_MAP: Record<string, string> = {
  "Opt-ins": "optins",
  "Registrants": "registrants",
  "Attended": "attended",
  "Applications": "apps",
  "Full Apps": "apps",
  "Partial Apps": "apps",
  "Calls Booked": "calls",
  "Webinar Bookings": "calls",
  "Calls Taken": "showed",
  "Closed": "wins",
  "Closes": "wins",
  "Cash": "wins",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtN = (n: number) => Math.round(n).toLocaleString();

type BlendedWebinarExportRow = { group: string; metric: string; value: string; notes?: string };

/** Dashboard's verified EOC cohort, after Book-a-Call precedence is applied. */
type CanonicalWebinarSalesMetrics = {
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  totalCashCollected: number;
};

export function blendedWebinarExportRows(
  webinars: WebinarFunnel[],
  sales: CanonicalWebinarSalesMetrics,
): BlendedWebinarExportRow[] {
  const total = (key: keyof WebinarFunnel) => webinars.reduce((sum, webinar) => sum + (Number(webinar[key]) || 0), 0);
  const registrants = total("totalRegistrants"); const attended = total("totalAttended");
  const optins = total("totalOptins"); const landingPageVisitors = total("landingPageVisitors");
  const apps = total("totalApps"); const calls = total("totalCalls");
  const adSpend = total("adSpend");
  const avgDuration = attended > 0 ? webinars.reduce((sum, webinar) => sum + (webinar.avgDuration || 0) * (webinar.totalAttended || 0), 0) / attended : 0;
  const rate = (numerator: number, denominator: number) => denominator > 0 ? fmtPct((numerator / denominator) * 100) : "N/A";
  const money = (value: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
  return [
    { group: "Scope", metric: "Webinars Included", value: fmtN(webinars.length), notes: "All saved webinars, all time" },
    { group: "Registration & attendance", metric: "Registrants", value: fmtN(registrants) },
    { group: "Registration & attendance", metric: "Attended", value: fmtN(attended) },
    { group: "Registration & attendance", metric: "Webinar Show Rate", value: rate(attended, registrants), notes: `${fmtN(attended)} attended / ${fmtN(registrants)} registrants` },
    { group: "Registration & attendance", metric: "Average Attendance Duration", value: attended > 0 ? `${Math.round(avgDuration)}m` : "N/A", notes: "Weighted by attended records" },
    { group: "Acquisition", metric: "Landing Page Visitors", value: fmtN(landingPageVisitors) },
    { group: "Acquisition", metric: "Opt-ins", value: fmtN(optins) },
    { group: "Acquisition", metric: "Opt-in Rate", value: rate(optins, landingPageVisitors), notes: `${fmtN(optins)} opt-ins / ${fmtN(landingPageVisitors)} visitors` },
    { group: "Acquisition", metric: "Paid Opt-ins", value: fmtN(total("paidOptins")) },
    { group: "Acquisition", metric: "Organic Opt-ins", value: fmtN(total("organicOptins")) },
    { group: "Funnel", metric: "Total Applications", value: fmtN(apps) },
    { group: "Funnel", metric: "Full Applications", value: fmtN(total("fullApps")) },
    { group: "Funnel", metric: "Partial Applications", value: fmtN(total("partialApps")) },
    { group: "Funnel", metric: "Qualified Applications", value: fmtN(total("qualifiedApps")) },
    { group: "Funnel", metric: "Opt-in → Application Rate", value: rate(apps, optins), notes: `${fmtN(apps)} applications / ${fmtN(optins)} opt-ins` },
    { group: "Funnel", metric: "Calls Booked", value: fmtN(calls), notes: "Distinct webinar-attributed booking conversions" },
    { group: "Funnel", metric: "Application → Call Rate", value: rate(calls, apps), notes: `${fmtN(calls)} calls / ${fmtN(apps)} applications` },
    { group: "Sales (verified EOC cohort)", metric: "EOC Calls Due", value: fmtN(sales.callsDue), notes: "Completed EOC records eligible to show; excludes cancelled and rescheduled outcomes" },
    { group: "Sales (verified EOC cohort)", metric: "Calls Taken", value: fmtN(sales.callsTaken), notes: "EOC calls that occurred" },
    { group: "Sales (verified EOC cohort)", metric: "Sales-Call Show Rate", value: rate(sales.callsTaken, sales.callsDue), notes: `${fmtN(sales.callsTaken)} taken / ${fmtN(sales.callsDue)} EOC calls due` },
    { group: "Sales (verified EOC cohort)", metric: "Closed Deals", value: fmtN(sales.callsClosed) },
    { group: "Sales (verified EOC cohort)", metric: "Close Rate", value: rate(sales.callsClosed, sales.callsTaken), notes: `${fmtN(sales.callsClosed)} closed / ${fmtN(sales.callsTaken)} taken` },
    { group: "Sales (verified EOC cohort)", metric: "Cash Collected", value: money(sales.totalCashCollected) },
    { group: "Sales (verified EOC cohort)", metric: "Average Cash per Close", value: sales.callsClosed > 0 ? money(sales.totalCashCollected / sales.callsClosed) : "N/A" },
    { group: "Efficiency", metric: "Ad Spend", value: money(adSpend) },
    { group: "Efficiency", metric: "ROAS", value: adSpend > 0 ? `${(sales.totalCashCollected / adSpend).toFixed(2)}x` : "N/A", notes: adSpend > 0 ? `${money(sales.totalCashCollected)} cash / ${money(adSpend)} spend` : "No recorded ad spend" },
  ];
}

function downloadExportBlob(blob: Blob, filename: string) {
  const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = filename; link.click(); URL.revokeObjectURL(link.href);
}

function webinarPdfText(value: string) { return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)").replace(/[–—→]/g, "-"); }

export function buildBlendedWebinarPdf(rows: BlendedWebinarExportRow[]) {
  const pageWidth = 792; const pageHeight = 612; const left = 48; const right = 48; const headerY = 518; const rowHeight = 18;
  const text = (x: number, y: number, value: string, size = 9, bold = false, color = "0.12 0.12 0.14") => `BT /F${bold ? "2" : "1"} ${size} Tf ${color} rg 1 0 0 1 ${x} ${y} Tm (${webinarPdfText(value)}) Tj ET`;
  const pages: string[] = [];
  for (let offset = 0; offset < rows.length; offset += 22) {
    const pageRows = rows.slice(offset, offset + 22); const continued = offset > 0;
    const commands = [text(left, 570, "Cr8Her Webinar Metrics Export", 16, true), text(left, 551, "Blended metrics across all saved webinars · All time", 9, false, "0.32 0.35 0.4"), text(left, 530, `All Webinar Metrics (Blended)${continued ? " (continued)" : ""}`, 11, true, "0.23 0.31 0.44"), "0.23 0.31 0.44 rg", `${left} ${headerY} ${pageWidth - left - right} 22 re f`, text(left + 8, headerY + 7, "Metric", 8, true, "1 1 1"), text(410, headerY + 7, "Value", 8, true, "1 1 1"), text(530, headerY + 7, "Calculation", 8, true, "1 1 1")];
    pageRows.forEach((row, index) => { const y = headerY - 22 - index * rowHeight; if (index === 0 || row.group !== pageRows[index - 1].group) commands.push(text(left + 8, y - 4, row.group.toUpperCase(), 7, true, "0.35 0.37 0.42")); commands.push(text(left + 150, y - 4, row.metric, 8), text(410, y - 4, row.value, 8, true), text(530, y - 4, row.notes || "", 7, false, "0.35 0.37 0.42"), `0.82 G 0.35 w ${left} ${y - rowHeight} m ${pageWidth - right} ${y - rowHeight} l S`); });
    commands.push(text(left, 42, "Sources: Webinar service for attendance/acquisition; Dashboard verified EOC cohort for sales-call metrics", 7, false, "0.35 0.37 0.4")); pages.push(commands.join("\n"));
  }
  const objects: string[] = ["<< /Type /Catalog /Pages 2 0 R >>", ""]; const pageNumbers = pages.map((_, index) => 3 + index * 2); objects[1] = `<< /Type /Pages /Kids [${pageNumbers.map((n) => `${n} 0 R`).join(" ")}] /Count ${pages.length} >>`;
  pages.forEach((content, index) => { const page = 3 + index * 2; objects[page - 1] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${pages.length * 2 + 3} 0 R /F2 ${pages.length * 2 + 4} 0 R >> >> /Contents ${page + 1} 0 R >>`; objects[page] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`; });
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>", "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>"); let pdf = "%PDF-1.4\n"; const offsets = [0]; objects.forEach((object, index) => { offsets.push(pdf.length); pdf += `${index + 1} 0 obj\n${object}\nendobj\n`; }); const xref = pdf.length; pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`; offsets.slice(1).forEach((value) => { pdf += `${String(value).padStart(10, "0")} 00000 n \n`; }); pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`; return new Blob([pdf], { type: "application/pdf" });
}

type MetricDelta = { kind: "zero" | "new" | "changed"; pct: number | null; direction: "up" | "down" | "flat" };

const calcMetricDelta = (current?: number | null, previous?: number | null): MetricDelta | null => {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) {
    if (current === 0) return { kind: "zero", pct: 0, direction: "flat" };
    return { kind: "new", pct: null, direction: "up" };
  }
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  return { kind: "changed", pct, direction: pct > 0.05 ? "up" : pct < -0.05 ? "down" : "flat" };
};

const formatMetricDelta = (delta: MetricDelta) => {
  if (delta.kind === "new") return "New vs prev";
  if (delta.kind === "zero") return "0.0% vs prev";
  const pct = delta.pct ?? 0;
  if (Math.abs(pct) < 0.05) return "0.0% vs prev";
  return `${pct > 0 ? "+" : ""}${pct.toFixed(1)}% vs prev`;
};

const formatDate = (s: string) => {
  const d = new Date(s + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

function fmtDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── OutcomeBadge ─────────────────────────────────────────────────────────────

function OutcomeBadge({ outcome }: { outcome: string }) {
  const closed = new Set(["Closed", "closed", "Close", "close", "Won", "won", "Sale", "sale"]);
  const noshow = new Set(["No Show", "Cancelled", "Rescheduled", "Cancelled by sales team"]);
  const isWon = closed.has(outcome);
  const isNoShow = noshow.has(outcome) || !outcome;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      isWon ? "bg-emerald-500/15 text-emerald-400" :
      isNoShow ? "bg-zinc-500/15 text-zinc-400" :
      "bg-amber-500/15 text-amber-400"
    }`}>
      {outcome || "No Show"}
    </span>
  );
}

// ─── Lead Timeline Drawer ─────────────────────────────────────────────────────

function LeadTimelineDrawer({ email, onClose }: { email: string; onClose: () => void }) {
  const [data, setData] = useState<LeadTimelineData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetch(`/api/lead-timeline?email=${encodeURIComponent(email)}`)
      .then((r) => r.json() as Promise<LeadTimelineData>)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const pri = branding.colors.primary;
  const sec = branding.colors.secondary;

  const events = data ? [
    ...data.optins.map((o) => ({ type: "optin" as const, date: o.date, data: o })),
    ...(data.webinarOptins ?? []).map((w) => ({ type: "webinar" as const, date: w.date, data: w })),
    ...(data.meetingBookings ?? []).map((m) => ({ type: "meeting" as const, date: m.bookedAt, data: m })),
    ...(data.application ? [{ type: "app" as const, date: data.application.date, data: data.application }] : []),
    ...data.calls.map((c) => ({ type: "call" as const, date: c.date, data: c })),
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return -1;
    if (!b.date) return 1;
    return a.date.localeCompare(b.date);
  }) : [];

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-card border-l border-border z-[70] flex flex-col overflow-hidden"
        style={{ boxShadow: "-8px 0 40px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground truncate">{data?.name || email}</h2>
              {data?.isClosed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Closed Won
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{email}</p>
            {data && (data.phone || data.country) && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {[data.phone, data.country].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <button onClick={onClose}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* KPI strip */}
        {data && (
          <div className="flex gap-3 px-5 py-3 border-b border-border shrink-0">
            {[
              { label: "Optins",   value: data.optins.length + (data.webinarOptins?.length ?? 0) },
              { label: "Applied",  value: data.application ? "Yes" : "No" },
              { label: "Meetings", value: (data.meetingBookings?.length ?? 0) + data.calls.length },
              { label: "Revenue",  value: data.totalRevenue > 0 ? `$${data.totalRevenue.toLocaleString()}` : "$0" },
            ].map((k) => (
              <div key={k.label} className="flex-1 min-w-0 rounded-xl px-3 py-2 text-center"
                style={{
                  background: `rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.06)`,
                  border: `1px solid rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.12)`,
                }}>
                <div className="text-[10px] text-muted-foreground font-medium mb-0.5">{k.label}</div>
                <div className="text-[15px] font-bold text-foreground tabular-nums">{String(k.value)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse mr-2" style={{ background: pri }} />
              Loading timeline…
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              No events found for this lead.
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="relative">
              <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border" />
              <div className="space-y-5">
                {events.map((ev, i) => {
                  const isOptin   = ev.type === "optin";
                  const isApp     = ev.type === "app";
                  const isCall    = ev.type === "call";
                  const isWebinar = ev.type === "webinar";
                  const isMeeting = ev.type === "meeting";
                  const isClosed  = isCall && new Set(["Closed","closed","Close","close","Won","won","Sale","sale"]).has((ev.data as TimelineCall).outcome);
                  const dotColor  = isOptin ? sec : isApp ? pri : isWebinar ? "#f59e0b" : isMeeting ? "#8b5cf6" : isClosed ? "#10b981" : "var(--muted-foreground)";
                  const label     = isOptin ? "Opted In" : isApp ? "Submitted Application" : isWebinar ? "Webinar Registration" : isMeeting ? "Meeting Booked" : "Call";

                  return (
                    <div key={i} className="flex gap-4 relative">
                      <div className="shrink-0 w-10 flex flex-col items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-background flex items-center justify-center z-10" style={{ background: dotColor }}>
                          {isOptin   && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2a5 5 0 110 10A5 5 0 0112 2zm0 12c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z"/></svg>}
                          {isApp     && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                          {isCall    && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>}
                          {isWebinar && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.07A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14v-4zM3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>}
                          {isMeeting && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 rounded-xl border border-border/60 bg-background/60 px-4 py-3 mb-1" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-foreground">{label}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{ev.date ? fmtDate(ev.date) : "—"}</span>
                        </div>
                        {isOptin && (() => {
                          const o = ev.data as TimelineOptin;
                          return (
                            <div className="space-y-0.5">
                              {o.source   && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{o.source}</span></div>}
                              {o.medium   && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{o.medium}</span></div>}
                              {o.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{o.campaign}</span></div>}
                              {o.sheet    && <div className="text-[10px] text-muted-foreground">Sheet: <span className="text-foreground/80">{o.sheet}</span></div>}
                            </div>
                          );
                        })()}
                        {isWebinar && (() => {
                          const w = ev.data as TimelineWebinarOptin;
                          return (
                            <div className="space-y-0.5">
                              {w.webinarName && <div className="text-[10px] text-muted-foreground">Webinar: <span className="text-foreground/80">{w.webinarName}</span></div>}
                              {w.source     && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{w.source}</span></div>}
                              {w.campaign   && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{w.campaign}</span></div>}
                            </div>
                          );
                        })()}
                        {isMeeting && (() => {
                          const m = ev.data as TimelineMeetingBooking;
                          return (
                            <div className="space-y-0.5">
                              {m.meetingAt && <div className="text-[10px] text-muted-foreground">Scheduled for: <span className="text-foreground/80">{fmtDate(m.meetingAt)}</span></div>}
                              {m.closer    && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{m.closer}</span></div>}
                            </div>
                          );
                        })()}
                        {isApp && (() => {
                          const a = ev.data as TimelineApplication;
                          return (
                            <div className="space-y-0.5">
                              {a.source       && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{a.source}</span></div>}
                              {a.medium       && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{a.medium}</span></div>}
                              {a.responseType && <div className="text-[10px] text-muted-foreground">Type: <span className="text-foreground/80">{a.responseType}</span></div>}
                            </div>
                          );
                        })()}
                        {isCall && (() => {
                          const c = ev.data as TimelineCall;
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <OutcomeBadge outcome={c.outcome} />
                                {c.cash > 0 && <span className="text-[10px] font-semibold text-emerald-400">${c.cash.toLocaleString()}</span>}
                              </div>
                              {c.offer  && <div className="text-[10px] text-muted-foreground">Offer: <span className="text-foreground/80">{c.offer}</span></div>}
                              {c.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{c.closer}</span></div>}
                              {c.setter && <div className="text-[10px] text-muted-foreground">Setter: <span className="text-foreground/80">{c.setter}</span></div>}
                              {c.notes  && <div className="text-[10px] text-muted-foreground mt-1 italic">"{c.notes}"</div>}
                              {c.fathomLink && (
                                <a href={c.fathomLink} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] mt-1"
                                  style={{ color: pri }}>
                                  View recording ↗
                                </a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// ─── Retention By Source Chart (with click-to-toggle lines) ──────────────────

function RetentionBySourceChart({
  data,
  sources,
  colors,
  pitchMinute,
  sourceTotals,
}: {
  data: RetentionBySourcePoint[];
  sources: string[];
  colors: string[];
  pitchMinute?: number | null;
  sourceTotals?: Record<string, number>;
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (src: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next;
    });
  };

  // Find data point closest to pitch minute for count labels
  const pitchPoint = pitchMinute != null && data.length > 0
    ? data.reduce((best, p) => Math.abs(p.minute - pitchMinute) < Math.abs(best.minute - pitchMinute) ? p : best)
    : null;

  const getCountAtPitch = (src: string): number | null => {
    if (!pitchPoint || !sourceTotals) return null;
    const pct = pitchPoint[src] as number | undefined;
    const total = sourceTotals[src];
    if (pct == null || total == null) return null;
    return Math.round(pct / 100 * total);
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">Retention by traffic source</p>

      {/* Clickable legend */}
      <div className="flex flex-wrap gap-2 mb-3">
        {sources.map((src, i) => {
          const isHidden = hidden.has(src);
          const color = colors[i % colors.length];
          const count = getCountAtPitch(src);
          return (
            <button
              key={src}
              onClick={() => toggle(src)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[10px] font-medium transition-all ${
                isHidden
                  ? "border-border/30 bg-muted/20 text-muted-foreground/40 line-through"
                  : "border-border bg-muted/40 text-foreground"
              }`}
            >
              <span
                className="w-3 h-0.5 rounded-full shrink-0"
                style={{ background: isHidden ? "rgba(255,255,255,0.15)" : color }}
              />
              {src}
              {count != null && !isHidden && pitchMinute != null && (
                <span className="text-muted-foreground/60 font-normal ml-0.5">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${v}m`} />
          <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            labelFormatter={(v) => `${v} min`}
            formatter={(v: number, name: string) => {
              if (hidden.has(name)) return [];
              const total = sourceTotals?.[name];
              const count = total != null ? Math.round(v / 100 * total) : null;
              return [count != null ? `${v.toFixed(1)}% (${count} people)` : `${v.toFixed(1)}%`, name];
            }}
          />
          {pitchMinute != null && (
            <ReferenceLine x={pitchMinute} stroke="rgba(255,255,255,0.35)" strokeDasharray="4 3"
              label={{ value: "▼ Pitch", position: "insideTopLeft", fontSize: 9, fill: "rgba(255,255,255,0.5)", dy: -2 }} />
          )}
          {sources.map((src, i) => (
            <Line
              key={src}
              type="monotone"
              dataKey={src}
              name={src}
              stroke={colors[i % colors.length]}
              strokeWidth={src === "all" ? 2 : 1.5}
              strokeDasharray={src === "all" ? "0" : undefined}
              dot={false}
              hide={hidden.has(src)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Cash By Source Pie Chart ────────────────────────────────────────────────

function CashBySourceChart({ data, title = "Cash Collected by Traffic Source/Creator" }: { data: CashBySourceItem[]; title?: string }) {
  const colors = branding.colors.chart;
  const total = data.reduce((s, d) => s + d.cash, 0);

  if (data.length === 0) {
    return (
      <div>
        <p className="text-xs text-muted-foreground mb-3">{title}</p>
        <div className="flex items-center justify-center py-4">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r="62" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="16" />
            <text x="70" y="65" textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.22)" fontSize="22" fontWeight="700">$0</text>
            <text x="70" y="85" textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.12)" fontSize="11">no data</text>
          </svg>
        </div>
      </div>
    );
  }

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
    cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number;
  }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">{title}</p>
      <div className="flex flex-col md:flex-row items-center gap-6">
        <ResponsiveContainer width={280} height={280}>
          <PieChart>
            <Pie
              data={data}
              dataKey="cash"
              nameKey="source"
              cx="50%"
              cy="50%"
              outerRadius={115}
              labelLine={false}
              label={renderLabel}
            >
              {data.map((entry, i) => (
                <Cell key={entry.source} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v: number, name: string) => [`$${v.toLocaleString()}`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col gap-3 min-w-0">
          {data.map((entry, i) => (
            <div key={entry.source} className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} />
              <span className="text-sm text-muted-foreground min-w-[90px]">{entry.source}</span>
              <span className="text-sm font-bold text-foreground">${entry.cash.toLocaleString()}</span>
              <span className="text-xs font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">
                {total > 0 ? `${((entry.cash / total) * 100).toFixed(0)}%` : ""}
              </span>
              <span className="text-xs text-muted-foreground/40">{entry.wins}w</span>
            </div>
          ))}
          <div className="mt-1 pt-2 border-t border-border/20 flex items-center gap-3">
            <span className="text-sm font-bold text-foreground">${total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/50">total</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AttendeesByCreatorItem { name: string; optins: number; joined: number; showRate: number; }

function AttendeesByCreatorPieChart({ data }: { data: AttendeesByCreatorItem[] }) {
  const colors = branding.colors.chart;
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, payload }: { cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; payload?: AttendeesByCreatorItem }) => {
    if (!payload || payload.joined === 0) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
    return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>{`${payload.showRate.toFixed(0)}% SR`}</text>;
  };
  return <div>
    <p className="text-xs text-muted-foreground mb-3">Leads that joined webinar by creator</p>
    <div className="flex flex-col md:flex-row items-center gap-6">
      <ResponsiveContainer width={280} height={280}><PieChart>
        <Pie data={data} dataKey="joined" nameKey="name" cx="50%" cy="50%" outerRadius={115} labelLine={false} label={renderLabel}>
          {data.map((entry, i) => <Cell key={entry.name} fill={colors[i % colors.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(value: number, _name: string, item: { payload: AttendeesByCreatorItem }) => [`${value.toLocaleString()} joined — ${item.payload.showRate.toFixed(1)}% show rate (${item.payload.optins.toLocaleString()} opt-ins)`, item.payload.name]} />
      </PieChart></ResponsiveContainer>
      <div className="flex flex-col gap-3 min-w-0">
        {data.map((entry, i) => <div key={entry.name} className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} />
          <span className="text-sm text-muted-foreground min-w-[90px] truncate max-w-[150px]" title={entry.name}>{entry.name}</span>
          <span className="text-xs font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">({entry.optins.toLocaleString()} opt-ins)</span>
          <span className="text-sm font-bold text-foreground">{entry.joined.toLocaleString()} joined</span>
          <span className="text-xs font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">{entry.showRate.toFixed(1)}% show rate</span>
        </div>)}
      </div>
    </div>
  </div>;
}

// ─── Opt-ins by UTM Pie Chart (reusable for creator + platform) ──────────────

function OptinsByUtmPieChart({ data, title }: { data: { name: string; count: number }[]; title: string }) {
  const colors = branding.colors.chart;
  const total = data.reduce((s, d) => s + d.count, 0);

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
    cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number;
  }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">{title}</p>
      <div className="flex flex-col md:flex-row items-center gap-6">
        <ResponsiveContainer width={280} height={280}>
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={115} labelLine={false} label={renderLabel}>
              {data.map((entry, i) => (
                <Cell key={entry.name} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v: number, name: string) => [`${v.toLocaleString()} opt-ins`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col gap-3 min-w-0">
          {data.map((entry, i) => (
            <div key={entry.name} className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} />
              <span className="text-sm text-muted-foreground min-w-[90px] truncate max-w-[160px]" title={entry.name}>{entry.name}</span>
              <span className="text-sm font-bold text-foreground">{entry.count.toLocaleString()}</span>
              <span className="text-xs font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">
                {total > 0 ? `${((entry.count / total) * 100).toFixed(0)}%` : ""}
              </span>
            </div>
          ))}
          <div className="mt-1 pt-2 border-t border-border/20 flex items-center gap-3">
            <span className="text-sm font-bold text-foreground">{total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/50">total</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatorDistributionPieChart({ data, title }: { data: { name: string; count: number }[]; title: string }) {
  const colors = branding.colors.chart;
  const total = data.reduce((sum, item) => sum + item.count, 0);
  return <div>
    <p className="text-xs text-muted-foreground mb-3">{title}</p>
    <div className="flex flex-col md:flex-row items-center gap-6">
      <ResponsiveContainer width={280} height={280}><PieChart><Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={115} labelLine={false}>
        {data.map((entry, i) => <Cell key={entry.name} fill={colors[i % colors.length]} />)}
      </Pie><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(value: number, name: string) => [`${value.toLocaleString()} (${total ? Math.round(value / total * 100) : 0}%)`, name]} /></PieChart></ResponsiveContainer>
      <div className="flex flex-col gap-3 min-w-0">{data.map((entry, i) => <div key={entry.name} className="flex items-center gap-3"><span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} /><span className="text-sm text-muted-foreground min-w-[90px] truncate max-w-[160px]">{entry.name}</span><span className="text-sm font-bold text-foreground">{entry.count.toLocaleString()}</span><span className="text-sm font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">{total ? `${Math.round(entry.count / total * 100)}%` : "0%"}</span></div>)}<div className="mt-1 pt-2 border-t border-border/20 text-sm font-bold text-foreground">{total.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">total</span></div></div>
    </div>
  </div>;
}

// ─── Funnel Chart (inverted triangle, clickable) ─────────────────────────────

function FunnelChart({
  stages,
  gradientId = "fgA",
  onStageClick,
  mutedValues = false,
}: {
  stages: FunnelStage[];
  gradientId?: string;
  onStageClick?: (label: string) => void;
  mutedValues?: boolean;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const valueFill = mutedValues ? "rgba(156,163,175,0.95)" : "rgba(255,255,255,0.95)";
  const subValueFill = mutedValues ? "rgba(156,163,175,0.75)" : "rgba(255,255,255,0.6)";

  const FULL_W = 370;
  const STAGE_H = 58;
  const TIP_W = 40;
  const GAP = 2;
  const LABEL_PAD = 130;
  const CONV_PAD = 126;
  const totalW = FULL_W + LABEL_PAD + CONV_PAD;
  const totalH = stages.length * (STAGE_H + GAP);

  // Fixed triangle shape — evenly stepped from full width down to a point, ignoring data values
  const n = stages.length;
  const stageWidths = stages.map((_, i) => {
    const t = n <= 1 ? 1 : i / (n - 1);
    return Math.round(FULL_W - t * (FULL_W - TIP_W));
  });

  return (
    <svg
      viewBox={`0 0 ${totalW} ${totalH}`}
      className="w-full"
      style={{ height: Math.min(totalH, 460) }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={branding.colors.gradient.start} stopOpacity={0.92} />
          <stop offset="100%" stopColor={branding.colors.gradient.end} stopOpacity={0.92} />
        </linearGradient>
      </defs>

      {stages.map((stage, i) => {
        const topW    = stageWidths[i];
        const bottomW = i < stages.length - 1 ? stageWidths[i + 1] : 0;
        const funnelLeft  = LABEL_PAD;
        const topLeft     = funnelLeft + (FULL_W - topW) / 2;
        const topRight    = funnelLeft + (FULL_W + topW) / 2;
        const bottomLeft  = funnelLeft + (FULL_W - bottomW) / 2;
        const bottomRight = funnelLeft + (FULL_W + bottomW) / 2;
        const y  = i * (STAGE_H + GAP);
        const cy = y + STAGE_H / 2;
        const pts = `${topLeft},${y} ${topRight},${y} ${bottomRight},${y + STAGE_H} ${bottomLeft},${y + STAGE_H}`;

        const convPct =
          i > 0 && i < stages.length - 1 && stages[i - 1].value > 0
            ? `${Math.round((stage.value / stages[i - 1].value) * 100)}%`
            : null;

        const opacity  = Math.max(0.5, 1 - i * 0.055);
        const isHovered = hoveredIdx === i;

        return (
          <g
            key={stage.label}
            onClick={() => onStageClick?.(stage.label)}
            onMouseEnter={() => onStageClick && setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: onStageClick ? "pointer" : "default" }}
          >
            <polygon points={pts} fill={`url(#${gradientId})`} opacity={opacity} />

            {/* Hover overlay */}
            {isHovered && <polygon points={pts} fill="white" opacity={0.1} />}

            {/* Stage label — follows the left funnel edge inward */}
            <text
              x={topLeft - 8} y={cy}
              textAnchor="end" dominantBaseline="middle"
              fontSize={11} fontWeight="600" fontFamily="sans-serif"
              fill="rgba(255,255,255,0.82)"
            >
              {stage.label}
            </text>

            {/* Primary value — follows the right funnel edge inward */}
            <text
              x={topRight + 8}
              y={stage.subLabel ? cy - 7 : cy}
              textAnchor="start" dominantBaseline="middle"
              fontSize={13} fontWeight="bold" fontFamily="sans-serif"
              fill={valueFill}
            >
              {stage.isCash ? fmt$(stage.value) : fmtN(stage.value)}
            </text>

            {stage.subLabel && (
              <text
                x={topRight + 8} y={cy + 9}
                textAnchor="start" dominantBaseline="middle"
                fontSize={9} fontFamily="sans-serif"
                fill={subValueFill}
              >
                {stage.subLabel}
              </text>
            )}

            {/* "click to view leads" hint on hover */}
            {isHovered && onStageClick && (
              <text
                x={LABEL_PAD + FULL_W / 2}
                y={cy}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={8} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.65)"
              >
                click to view leads
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Funnel Leads Modal ───────────────────────────────────────────────────────

interface FunnelLeadsModalProps {
  webinarId: string;
  webinarName: string;
  stageLabel: string;
  stageKey: string;
  traffic: "all" | "paid" | "organic";
  onClose: () => void;
  onLeadClick: (email: string) => void;
}

function FunnelLeadsModal({
  webinarId, webinarName, stageLabel, stageKey, traffic, onClose, onLeadClick,
}: FunnelLeadsModalProps) {
  const [leads, setLeads] = useState<StageLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    setLeads([]);
    fetch(`/api/webinar2/stage-leads/${webinarId}/${stageKey}?traffic=${traffic}`)
      .then((r) => r.json())
      .then((d) => { setLeads(d.leads || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [webinarId, stageKey, traffic]);

  const filtered = search
    ? leads.filter(
        (l) =>
          l.email.toLowerCase().includes(search.toLowerCase()) ||
          l.name.toLowerCase().includes(search.toLowerCase())
      )
    : leads;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl w-full max-w-lg z-10 flex flex-col"
        style={{ maxHeight: "80vh" }}
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm font-bold text-foreground">{stageLabel} Leads</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {webinarName}
              {!loading && ` · ${leads.length.toLocaleString()} leads`}
              {traffic !== "all" && ` · ${traffic}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-border/50 shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
            autoFocus
          />
        </div>

        {/* Lead list */}
        <div className="flex-1 overflow-y-auto py-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              {search ? "No leads match your search" : "No leads for this stage"}
            </div>
          ) : (
            <>
              {filtered.map((lead) => (
                <button
                  key={lead.email}
                  onClick={() => onLeadClick(lead.email)}
                  className="w-full text-left px-4 py-2.5 hover:bg-muted/30 transition-colors flex items-center justify-between gap-3 group border-b border-border/20 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {lead.name || lead.email}
                    </div>
                    {lead.name && (
                      <div className="text-[11px] text-muted-foreground truncate">{lead.email}</div>
                    )}
                    {lead.source && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{lead.source}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {lead.webinarOptinCount != null && lead.webinarOptinCount > 0 && (
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          lead.webinarOptinCount >= 3
                            ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                            : "bg-muted text-muted-foreground"
                        }`}
                        title={`${lead.webinarOptinCount} webinar optin${lead.webinarOptinCount !== 1 ? "s" : ""}`}
                      >
                        ×{lead.webinarOptinCount}
                      </span>
                    )}
                    {lead.closedAt && (
                      <span className="text-[10px] text-muted-foreground">{lead.closedAt}</span>
                    )}
                    {lead.outcome && <OutcomeBadge outcome={lead.outcome} />}
                    {lead.cash != null && lead.cash > 0 && (
                      <span className="text-xs font-semibold text-emerald-400">
                        ${lead.cash.toLocaleString()}
                      </span>
                    )}
                    <svg className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
              {leads.length >= 500 && search === "" && (
                <p className="text-center py-3 text-[10px] text-muted-foreground">
                  Showing first 500 leads · use search to find specific leads
                </p>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Cancelled Leads Modal ────────────────────────────────────────────────────

function CancelledLeadsModal({
  webinarId, webinarName, traffic, onClose,
}: { webinarId: string; webinarName: string; traffic: "all" | "paid" | "organic"; onClose: () => void }) {
  const [bySales, setBySales] = useState<StageLead[]>([]);
  const [byLead, setByLead] = useState<StageLead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/webinar2/cancelled-leads/${webinarId}?traffic=${traffic}`)
      .then((r) => r.json())
      .then((d) => { setBySales(d.bySales || []); setByLead(d.byLead || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [webinarId, traffic]);

  const col = (leads: StageLead[], heading: string) => (
    <div className="flex-1 min-w-0 flex flex-col">
      <div className="px-4 py-2.5 border-b border-border/50 shrink-0">
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{heading}</span>
        {!loading && <span className="ml-1.5 text-[11px] text-muted-foreground/60">({leads.length})</span>}
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">None</div>
        ) : leads.map((lead) => (
          <div key={lead.email} className="px-4 py-2.5 border-b border-border/20 last:border-0">
            <div className="text-sm font-medium text-foreground truncate">{lead.email}</div>
            {lead.outcome && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{lead.outcome}</div>}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl w-full max-w-2xl z-10 flex flex-col"
        style={{ maxHeight: "80vh" }}
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm font-bold text-foreground">Cancelled Calls</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{webinarName}{traffic !== "all" && ` · ${traffic}`}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-1 overflow-hidden divide-x divide-border/40">
          {col(bySales, "Cancelled by Team")}
          {col(byLead, "Cancelled by Lead")}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Zoom Upload Modal ────────────────────────────────────────────────────────

interface UploadModalProps {
  webinarId: string;
  webinarName: string;
  onClose: () => void;
  onSuccess: (count: number) => void;
}

function ZoomUploadModal({ webinarId, webinarName, onClose, onSuccess }: UploadModalProps) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [result, setResult] = useState<{ imported: number; attended: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    if (!f.name.endsWith(".csv")) { setErrorMsg("Please upload a CSV file."); return; }
    setFile(f);
    setErrorMsg("");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/webinar2/zoom-upload/${webinarId}`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success) {
        setResult({ imported: data.imported, attended: data.attended });
        setStatus("success");
        onSuccess(data.imported);
      } else {
        setErrorMsg(data.error || "Upload failed");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Network error");
      setStatus("error");
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md z-10"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Upload Zoom Report</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{webinarName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
        </div>

        {status === "success" ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-foreground font-semibold">Upload complete</p>
            <p className="text-muted-foreground text-sm mt-1">
              {result?.imported} registrants imported · {result?.attended} attended
            </p>
            <button onClick={onClose} className="mt-4 px-5 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium">
              Done
            </button>
          </div>
        ) : (
          <>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragging ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-border hover:border-border/70"
              }`}
            >
              <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
              <div className="text-2xl mb-2">📂</div>
              {file ? (
                <p className="text-sm text-foreground font-medium">{file.name}</p>
              ) : (
                <>
                  <p className="text-sm text-foreground font-medium">Drop CSV or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">Zoom attendee/registrant report</p>
                </>
              )}
            </div>
            {errorMsg && <p className="text-red-400 text-xs mt-2">{errorMsg}</p>}
            <p className="text-xs text-muted-foreground mt-3">
              Required columns: Attended, Email, First Name, Last Name, Time in Session (minutes), Registration Time, Join Time, Leave Time
            </p>
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || status === "uploading"}
                className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              >
                {status === "uploading" ? "Uploading…" : "Upload"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Add Webinar Modal ────────────────────────────────────────────────────────

interface AddWebinarModalProps {
  onClose: () => void;
  onSuccess: (webinarId: string) => void;
}

function AddWebinarModal({ onClose, onSuccess }: AddWebinarModalProps) {
  const [date, setDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [detectedDate, setDetectedDate] = useState<string | null>(null);
  const [detectedStartTime, setDetectedStartTime] = useState<string | null>(null);
  const [marketingStartDate, setMarketingStartDate] = useState("");
  const [marketingStartTime, setMarketingStartTime] = useState("");
  const [marketingEndDate, setMarketingEndDate] = useState("");
  const [marketingEndTime, setMarketingEndTime] = useState("");
  const [pitchStartTime, setPitchStartTime] = useState("");
  const [webinarEndTime, setWebinarEndTime] = useState("");
  const [audienceType, setAudienceType] = useState<"warm" | "cold" | "">("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ webinarId: string; imported: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const effectiveDate = date || detectedDate || "";

  const pad2 = (n: number) => String(n).padStart(2, "0");
  const dateInputValue = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const timeInputValue = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const openNativePicker = (input: HTMLInputElement) => {
    try {
      input.showPicker?.();
    } catch {
      // Some browsers only allow showPicker from direct user gestures; normal input focus still works.
    }
  };

  const combineDateTime = (datePart: string, timePart: string) => datePart && timePart ? `${datePart}T${timePart}` : "";

  const parseCsvLine = (line: string) => {
    const cells: string[] = [];
    let current = "";
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      const next = line[i + 1];
      if (ch === '"' && quoted && next === '"') { current += '"'; i++; }
      else if (ch === '"') quoted = !quoted;
      else if (ch === "," && !quoted) { cells.push(current.trim()); current = ""; }
      else current += ch;
    }
    cells.push(current.trim());
    return cells.map((cell) => cell.replace(/^"|"$/g, ""));
  };

  const parseZoomStart = (raw: string): { date: string; time: string } | null => {
    const value = raw.trim();
    const m = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4}).*?(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (m) {
      const month = m[1].padStart(2, "0");
      const day = m[2].padStart(2, "0");
      const year = m[3].length === 2 ? `20${m[3]}` : m[3];
      let hour = Number(m[4]);
      const minute = m[5];
      const ampm = m[6]?.toUpperCase();
      if (ampm === "PM" && hour < 12) hour += 12;
      if (ampm === "AM" && hour === 12) hour = 0;
      return { date: `${year}-${month}-${day}`, time: `${pad2(hour)}:${minute}` };
    }
    const d = new Date(value);
    if (!isNaN(d.getTime())) return { date: dateInputValue(d), time: timeInputValue(d) };
    return null;
  };

  // Sniff date/time from CSV header client-side so user sees it before submitting
  const handleFileChange = (f: File) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/).slice(0, 6);
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].toLowerCase().includes("topic") && i + 1 < lines.length) {
          const headerCols = parseCsvLine(lines[i]).map((h) => h.trim().toLowerCase());
          const dataRow = parseCsvLine(lines[i + 1]);
          const startIdx = headerCols.findIndex((h) => h.includes("actual start time") || h.includes("start time"));
          if (startIdx >= 0 && dataRow[startIdx]) {
            const parsed = parseZoomStart(dataRow[startIdx]);
            if (parsed) {
              setDetectedDate(parsed.date);
              setDetectedStartTime(parsed.time);
              if (!date) setDate(parsed.date);
            }
          }
          break;
        }
      }
    };
    reader.readAsText(f);
  };

  const handleSubmit = async () => {
    if (!effectiveDate && !file) { setError("Upload a Zoom CSV or enter a date"); return; }
    setStatus("loading");
    setError("");
    try {
      const marketingStartPartial = Boolean(marketingStartDate || marketingStartTime) && !(marketingStartDate && marketingStartTime);
      const marketingEndPartial = Boolean(marketingEndDate || marketingEndTime) && !(marketingEndDate && marketingEndTime);
      if (marketingStartPartial || marketingEndPartial) {
        setError("Marketing start/end need both a date and a time, or leave that field blank.");
        setStatus("error");
        return;
      }
      const marketingStart = combineDateTime(marketingStartDate, marketingStartTime);
      const marketingEnd = combineDateTime(marketingEndDate, marketingEndTime);

      const fd = new FormData();
      if (effectiveDate) fd.append("date", effectiveDate);
      if (file) fd.append("file", file);
      if (marketingStart) fd.append("marketingStart", marketingStart);
      if (marketingEnd) fd.append("marketingEnd", marketingEnd);
      if (pitchStartTime) fd.append("pitchStartTime", pitchStartTime);
      if (webinarEndTime) fd.append("webinarEndTime", webinarEndTime);
      if (audienceType) fd.append("audienceType", audienceType);
      const res = await fetch("/api/webinar2/create", { method: "POST", body: fd });
      const data = await res.json() as { success: boolean; webinarId?: string; imported?: number; error?: string };
      if (data.success && data.webinarId) {
        setResult({ webinarId: data.webinarId, imported: data.imported ?? 0 });
        setStatus("success");
      } else {
        setError(data.error ?? "Failed to create webinar");
        setStatus("error");
      }
    } catch {
      setError("Network error");
      setStatus("error");
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Add New Webinar</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Upload the Zoom attendee report — date and timing can be adjusted below</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
        </div>

        {status === "success" && result ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-foreground font-semibold">{result.webinarId} created</p>
            {result.imported > 0 && (
              <p className="text-muted-foreground text-sm mt-1">{result.imported} unique attendees imported</p>
            )}
            <button
              onClick={() => onSuccess(result!.webinarId)}
              className="mt-4 px-5 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium"
            >
              View Webinar
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">
                  Zoom Attendee Report CSV <span className="text-[var(--brand-primary)]">*</span>
                </label>
                <div
                  onClick={() => inputRef.current?.click()}
                  className="border border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-[var(--brand-primary)]/50 transition-colors"
                >
                  <input ref={inputRef} type="file" accept=".csv" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChange(f); }} />
                  {file ? (
                    <div>
                      <p className="text-sm text-foreground">{file.name}</p>
                      {detectedDate && (
                        <p className="text-xs text-[var(--brand-primary)] mt-1">
                          Date detected: {detectedDate}{detectedStartTime ? ` · start ${detectedStartTime} ET` : ""}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Click to upload — date/start time will be auto-detected</p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">
                  Webinar Date <span className="text-muted-foreground/60">(override if needed)</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onClick={(e) => openNativePicker(e.currentTarget)}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Audience Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["", "warm", "cold"] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setAudienceType(opt)}
                      className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                        audienceType === opt
                          ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                          : "border-border bg-background text-muted-foreground hover:border-[var(--brand-primary)]/40"
                      }`}
                    >
                      {opt === "" ? "Unknown" : opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-[var(--brand-primary)]/20 bg-[var(--brand-primary)]/5 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-foreground">Attribution Timing</p>
                    <p className="text-[11px] text-muted-foreground mt-1">All times are interpreted as Eastern time. Click any date or time field to use the browser/native picker.</p>
                    {detectedStartTime && (
                      <p className="text-[11px] text-[var(--brand-primary)] mt-1">Zoom start detected: {detectedStartTime} ET.</p>
                    )}
                  </div>
                  <span className="shrink-0 rounded-full border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">ET</span>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-background/30 p-3">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div>
                    <p className="text-xs font-medium text-foreground">Marketing Window</p>
                    <p className="text-[11px] text-muted-foreground">Controls opt-in attribution + GA4 opt-in-rate window.</p>
                  </div>
                  <button type="button" onClick={() => { setMarketingStartDate(""); setMarketingStartTime(""); setMarketingEndDate(""); setMarketingEndTime(""); }} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">Clear</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1.5">Marketing Start <span className="text-muted-foreground/60">(ET)</span></label>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="date" value={marketingStartDate} onClick={(e) => openNativePicker(e.currentTarget)} onChange={(e) => setMarketingStartDate(e.target.value)}
                        aria-label="Marketing start date"
                        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer" />
                      <input type="time" value={marketingStartTime} onClick={(e) => openNativePicker(e.currentTarget)} onChange={(e) => setMarketingStartTime(e.target.value)}
                        aria-label="Marketing start time"
                        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1.5">Marketing End <span className="text-muted-foreground/60">(ET)</span></label>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="date" value={marketingEndDate} onClick={(e) => openNativePicker(e.currentTarget)} onChange={(e) => setMarketingEndDate(e.target.value)}
                        aria-label="Marketing end date"
                        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer" />
                      <input type="time" value={marketingEndTime} onClick={(e) => openNativePicker(e.currentTarget)} onChange={(e) => setMarketingEndTime(e.target.value)}
                        aria-label="Marketing end time"
                        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-background/30 p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <label className="text-xs text-muted-foreground">Pitch Start Time <span className="text-muted-foreground/60">(ET)</span></label>
                    <button type="button" onClick={() => setPitchStartTime("")} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">Clear</button>
                  </div>
                  <input type="time" value={pitchStartTime} onClick={(e) => openNativePicker(e.currentTarget)} onChange={(e) => setPitchStartTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer" />
                  <p className="text-[11px] text-muted-foreground mt-2">Used to calculate retention at pitch from the Zoom actual start time.</p>
                </div>

                <div className="rounded-xl border border-border bg-background/30 p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <label className="text-xs text-muted-foreground">Webinar End Time <span className="text-muted-foreground/60">(ET)</span></label>
                    <button type="button" onClick={() => setWebinarEndTime("")} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">Clear</button>
                  </div>
                  <input type="time" value={webinarEndTime} onClick={(e) => openNativePicker(e.currentTarget)} onChange={(e) => setWebinarEndTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)] cursor-pointer" />
                  <p className="text-[11px] text-muted-foreground mt-2">Controls on-webby vs post-webby application buckets.</p>
                </div>
              </div>
            </div>
            {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
            <div className="flex gap-2 mt-5">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={(!effectiveDate && !file) || status === "loading"}
                className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              >
                {status === "loading" ? "Creating…" : "Create Webinar"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Metric benchmark targets ─────────────────────────────────────────────────
// Shared with Dashboard → Velocity Metrics (src/config/webinar-targets.ts), so both pages score the same way.

type MetricTargetMap = WebinarTargetMap;
const SALES_TARGETS: MetricTargetMap = WEBINAR_SALES_TARGETS;
const ON_WEBINAR_TARGETS: MetricTargetMap = WEBINAR_ON_WEBINAR_TARGETS;
const ZOOM_TARGETS: MetricTargetMap = WEBINAR_ZOOM_TARGETS;

// ─── Main Component ───────────────────────────────────────────────────────────

export function Webinars() {
  const { theme } = useTheme();
  const isCrimson = theme === "crimson";
  const webinarMetricValueClass = isCrimson ? "text-gray-400" : "text-foreground";
  const [webinars, setWebinars] = useState<WebinarDef[]>([]);
  const [exportingBlendedWebinars, setExportingBlendedWebinars] = useState<"csv" | "xlsx" | "pdf" | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [funnelCache, setFunnelCache] = useState<Record<string, WebinarFunnel>>({});
  const [retentionCache, setRetentionCache] = useState<Record<string, RetentionPoint[]>>({});
  const [retentionBySourceCache, setRetentionBySourceCache] = useState<Record<string, RetentionBySourcePoint[]>>({});
  const [sourceTotalsCache, setSourceTotalsCache] = useState<Record<string, Record<string, number>>>({});
  const [cashBySourceCache, setCashBySourceCache] = useState<Record<string, CashBySourceItem[]>>({});
  const [cashByCreatorCache, setCashByCreatorCache] = useState<Record<string, CashBySourceItem[]>>({});
  const [creatorDistributionsCache, setCreatorDistributionsCache] = useState<Record<string, CreatorDistributions>>({});
  const [cashGroupBy, setCashGroupBy] = useState<"platform" | "creator">("platform");
  const [applicationPieType, setApplicationPieType] = useState<"all" | "full" | "partial">("all");
  const [optinsByUtmCache, setOptinsByUtmCache] = useState<Record<string, { byCreator: { name: string; count: number }[]; byPlatform: { name: string; count: number }[] }>>({});
  const [attendeesByCreatorCache, setAttendeesByCreatorCache] = useState<Record<string, AttendeesByCreatorItem[]>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [funnelMode, setFunnelMode] = useState<"all" | "compare">("all"); // single-view only
  const [cardTraffic, setCardTraffic] = useState<Record<string, "all" | "paid" | "organic">>({});
  const [uploadModal, setUploadModal] = useState<string | null>(null);
  const [addWebinarModal, setAddWebinarModal] = useState(false);
  const [adSpendInput, setAdSpendInput] = useState<Record<string, string>>({});
  const [savingAdSpend, setSavingAdSpend] = useState<string | null>(null);
  const [calendarInput, setCalendarInput] = useState<Record<string, string>>({});
  const [whatsappInput, setWhatsappInput] = useState<Record<string, string>>({});
  const [savingEngagement, setSavingEngagement] = useState<string | null>(null);
  const [stageLeadsModal, setStageLeadsModal] = useState<{
    webinarId: string;
    label: string;
    stageKey: string;
    traffic: "all" | "paid" | "organic";
  } | null>(null);
  const [investmentModal, setInvestmentModal] = useState<{
    webinarName: string;
    breakdown: Record<string, number>;
    qualifiedApps: number;
    totalFullApps: number;
  } | null>(null);
  const [timelineEmail, setTimelineEmail] = useState<string | null>(null);

  // Webinar list filters
  const [funnelFilter, setFunnelFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [creatorFilter, setCreatorFilter] = useState("");

  // Unique filter options — case-insensitive dedup (WhatsApp + whatsapp = same option)
  const buildFilterOptions = (values: (string | undefined)[]): { key: string; label: string }[] => {
    const seen = new Map<string, string>();
    for (const v of values) {
      if (!v?.trim()) continue;
      const key = v.toLowerCase().trim();
      if (!seen.has(key)) seen.set(key, v.trim());
    }
    return [...seen.entries()].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
  };
  const uniqueFunnels = buildFilterOptions(webinars.map((w) => w.funnel));
  const uniquePlatforms = buildFilterOptions(webinars.map((w) => w.platform));
  const uniqueCreators = buildFilterOptions(webinars.map((w) => w.creator));

  // Filtered webinar list — compare normalized to filter key
  const filteredWebinars = webinars.filter((w) => {
    if (funnelFilter && (w.funnel || "").toLowerCase().trim() !== funnelFilter) return false;
    if (platformFilter && (w.platform || "").toLowerCase().trim() !== platformFilter) return false;
    if (creatorFilter && (w.creator || "").toLowerCase().trim() !== creatorFilter) return false;
    return true;
  });

  // Enhanced metrics state
  const [trafficFilter, setTrafficFilter] = useState<"all" | "paid" | "organic">("all");
  const [appTypeFilter, setAppTypeFilter] = useState<"all" | "full" | "partial">("all");
  const [segmentSource, setSegmentSource] = useState("");
  const [segmentMedium, setSegmentMedium] = useState("");
  const [segmentCampaign, setSegmentCampaign] = useState("");
  const [segmentContent, setSegmentContent] = useState("");
  const [selectedInvestmentLevels, setSelectedInvestmentLevels] = useState<string[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [metricsView, setMetricsView] = useState<"total" | "on-webby" | "post-webby">("total");
  const [viewMode, setViewMode] = useState<"card" | "list">("card");
  const [enhancedData, setEnhancedData] = useState<Record<string, EnhancedWebinarData>>({});
  const [enhancedLoading, setEnhancedLoading] = useState<Set<string>>(new Set());
  const [pitchMinuteInput, setPitchMinuteInput] = useState<Record<string, string>>({});
  const [savingPitchMinute, setSavingPitchMinute] = useState<string | null>(null);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());
  const [hiddenOnWebinarMetrics, setHiddenOnWebinarMetrics] = useState<Set<string>>(new Set());

  // Sales card drag-to-reorder (persisted in localStorage)
  const [salesCardOrder, setSalesCardOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("cr8her_sales_card_order") ?? "[]") as string[]; } catch { return []; }
  });
  const [dragSrcIdx, setDragSrcIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  // Card highlights: key = `${webinarId}-${cardLabel}`, value = "green" | "red" — persisted in localStorage
  const [cardHighlights, setCardHighlights] = useState<Record<string, "green" | "red">>(() => {
    try { return JSON.parse(localStorage.getItem("cr8her_card_highlights") ?? "{}") as Record<string, "green" | "red">; } catch { return {}; }
  });
  const toggleCardHighlight = (webinarId: string, label: string, color: "green" | "red") => {
    const key = `${webinarId}-${label}`;
    setCardHighlights((prev) => {
      const next = { ...prev };
      if (next[key] === color) { delete next[key]; } else { next[key] = color; }
      localStorage.setItem("cr8her_card_highlights", JSON.stringify(next));
      return next;
    });
  };
  const clearCardHighlight = (webinarId: string, label: string) => {
    const key = `${webinarId}-${label}`;
    setCardHighlights((prev) => {
      const next = { ...prev };
      delete next[key];
      localStorage.setItem("cr8her_card_highlights", JSON.stringify(next));
      return next;
    });
  };

  // On-Webinar card drag-to-reorder (persisted in localStorage)
  const [onWebinarCardOrder, setOnWebinarCardOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("cr8her_on_webinar_card_order") ?? "[]") as string[]; } catch { return []; }
  });
  const [dragOnWebinarSrcIdx, setDragOnWebinarSrcIdx] = useState<number | null>(null);
  const [dragOnWebinarOverIdx, setDragOnWebinarOverIdx] = useState<number | null>(null);

  const [cancelledModal, setCancelledModal] = useState<{ webinarId: string; traffic: "all" | "paid" | "organic" } | null>(null);
  const [regTimingCache, setRegTimingCache] = useState<Record<string, RegTimingPoint[]>>({});

  const toggleMetric = (label: string) => {
    setHiddenMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const toggleOnWebinarMetric = (label: string) => {
    setHiddenOnWebinarMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const [hiddenLowTicketMetrics, setHiddenLowTicketMetrics] = useState<Set<string>>(new Set());

  const toggleLowTicketMetric = (label: string) => {
    setHiddenLowTicketMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Load webinar list on mount
  useEffect(() => {
    fetch("/api/webinar2/list")
      .then((r) => r.json())
      .then((d: { success: boolean; webinars: WebinarDef[] }) => {
        if (d.success) {
          const sorted = [...d.webinars].reverse();
          setWebinars(sorted);
          if (sorted.length) setSelectedIds([sorted[0].id]);
        }
      })
      .catch(console.error);
  }, []);

  const downloadBlendedWebinarExport = async (format: "csv" | "xlsx" | "pdf") => {
    setExportingBlendedWebinars(format);
    try {
      const [funnelResponse, dashboardResponse] = await Promise.all([
        fetch("/api/webinar2/all"),
        fetch("/api/dashboard/summary?funnelType=webinar&dateField=timestamp"),
      ]);
      const [body, dashboardBody] = await Promise.all([
        funnelResponse.json() as Promise<{ success: boolean; webinars?: WebinarFunnel[]; error?: string }> ,
        dashboardResponse.json() as Promise<{ success: boolean; data?: { metrics?: CanonicalWebinarSalesMetrics }; error?: string }> ,
      ]);
      const sales = dashboardBody.data?.metrics;
      if (!funnelResponse.ok || !body.success || !body.webinars?.length) throw new Error(body.error || "No webinar data is available for export");
      if (!dashboardResponse.ok || !dashboardBody.success || !sales) throw new Error(dashboardBody.error || "Verified webinar sales metrics are unavailable for export");
      const rows = blendedWebinarExportRows(body.webinars, sales);
      const filename = "cr8her-all-webinar-blended-metrics";
      if (format === "pdf") {
        downloadExportBlob(buildBlendedWebinarPdf(rows), `${filename}.pdf`);
      } else {
        const matrix = [["Metric Group", "Metric", "Value", "Calculation / Notes"], ...rows.map((row) => [row.group, row.metric, row.value, row.notes || ""])];
        if (format === "csv") {
          downloadExportBlob(new Blob([matrix.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv;charset=utf-8" }), `${filename}.csv`);
        } else {
          const worksheet = XLSX.utils.aoa_to_sheet(matrix); worksheet["!cols"] = [{ wch: 28 }, { wch: 32 }, { wch: 18 }, { wch: 48 }];
          const workbook = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(workbook, worksheet, "Blended All-Time"); XLSX.writeFile(workbook, `${filename}.xlsx`);
        }
      }
    } catch (error) {
      console.error("Unable to export blended webinar metrics:", error);
      window.alert(error instanceof Error ? error.message : "Unable to export blended webinar metrics");
    } finally {
      setExportingBlendedWebinars(null);
    }
  };

  const loadWebinar = useCallback(async (id: string) => {
    setLoadingIds((prev) => new Set([...prev, id]));
    try {
      const [fRes, rRes, rbsRes, cbsRes, cbcRes, creatorDistRes, rtRes, utmRes, attendeesCreatorRes] = await Promise.all([
        fetch(`/api/webinar2/funnel/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/retention/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/retention-by-source/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/cash-by-source/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/cash-by-source/${id}?groupBy=creator`).then((r) => r.json()),
        fetch(`/api/webinar2/creator-distributions/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/reg-timing/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/optins-by-utm/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/attendees-by-creator/${id}`).then((r) => r.json()),
      ]);
      if (fRes.success) {
        setFunnelCache((prev) => ({ ...prev, [id]: fRes.funnel }));
        if (fRes.funnel.adSpend > 0) {
          setAdSpendInput((prev) => ({ ...prev, [id]: String(fRes.funnel.adSpend) }));
        }
      }
      if (rRes.success) setRetentionCache((prev) => ({ ...prev, [id]: rRes.retention }));
      if (rbsRes.success && rbsRes.retention?.length > 0) {
        setRetentionBySourceCache((prev) => ({ ...prev, [id]: rbsRes.retention }));
        if (rbsRes.sourceTotals) {
          setSourceTotalsCache((prev) => ({ ...prev, [id]: rbsRes.sourceTotals as Record<string, number> }));
        }
      }
      if (cbsRes.success && cbsRes.data?.length > 0) {
        setCashBySourceCache((prev) => ({ ...prev, [id]: cbsRes.data }));
      }
      if (cbcRes.success) setCashByCreatorCache((prev) => ({ ...prev, [id]: cbcRes.data || [] }));
      if (creatorDistRes.success && creatorDistRes.data) setCreatorDistributionsCache((prev) => ({ ...prev, [id]: creatorDistRes.data }));
      if (utmRes.success && utmRes.data) {
        setOptinsByUtmCache((prev) => ({ ...prev, [id]: utmRes.data }));
      }
      if (attendeesCreatorRes.success && attendeesCreatorRes.data) {
        setAttendeesByCreatorCache((prev) => ({ ...prev, [id]: attendeesCreatorRes.data }));
      }
      if (rtRes.success && rtRes.points?.length > 0) {
        setRegTimingCache((prev) => ({ ...prev, [id]: rtRes.points }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  const primaryId = selectedIds[0] ?? null;
  // Sheets compare one selected webinar against the three immediately preceding saved webinars.
  // `webinars` is newest-first, so later indexes are chronologically earlier webinars.
  const sheetComparisonIds = useMemo(() => {
    const index = webinars.findIndex((webinar) => webinar.id === primaryId);
    return index < 0 ? [] : webinars.slice(index, index + 4).map((webinar) => webinar.id);
  }, [webinars, primaryId]);

  // Load funnels for newly selected webinars
  useEffect(() => {
    for (const id of selectedIds) {
      if (!funnelCache[id] && !loadingIds.has(id)) loadWebinar(id);
    }
  }, [selectedIds, funnelCache, loadingIds, loadWebinar]);

  // Fetch filter options when primary webinar changes
  useEffect(() => {
    if (!primaryId) return;
    setFilterOptions(null);
    setSegmentSource("");
    setSegmentMedium("");
    setSegmentCampaign("");
    setSegmentContent("");
    setSelectedInvestmentLevels([]);
    fetch(`/api/webinar2/filter-options/${primaryId}`)
      .then((r) => r.json())
      .then((d: { success: boolean; options: FilterOptions }) => {
        if (d.success) setFilterOptions(d.options);
      })
      .catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryId]);

  // Load enhanced metrics when webinar or filters change
  const loadEnhanced = useCallback(async (
    id: string, traffic: string, appType: string,
    src = "", med = "", campaign = "", content = "", investLevels: string[] = [],
  ) => {
    const investKey = investLevels.join("|");
    const cacheKey = `${id}-${traffic}-${appType}-${src}-${med}-${campaign}-${content}-${investKey}`;
    setEnhancedLoading((prev) => new Set([...prev, id]));
    try {
      const params = new URLSearchParams({ traffic, appType });
      if (src) params.set("segmentSource", src);
      if (med) params.set("segmentMedium", med);
      if (campaign) params.set("segmentCampaign", campaign);
      if (content) params.set("segmentContent", content);
      if (investLevels.length > 0) params.set("investmentLevels", investLevels.join("|"));
      const res = await fetch(`/api/webinar2/enhanced/${id}?${params}`);
      const d = await res.json() as { success: boolean; data: EnhancedWebinarData };
      if (d.success) {
        setEnhancedData((prev) => ({ ...prev, [cacheKey]: d.data }));
        if (d.data.onWebinar.pitchMinute) {
          setPitchMinuteInput((prev) => ({ ...prev, [id]: String(d.data.onWebinar.pitchMinute) }));
        }
        if (d.data.addToCalendar > 0) {
          setCalendarInput((prev) => ({ ...prev, [id]: String(d.data.addToCalendar) }));
        }
        if (d.data.joinedWhatsApp > 0) {
          setWhatsappInput((prev) => ({ ...prev, [id]: String(d.data.joinedWhatsApp) }));
        }
      }
    } catch (e) {
      console.error("Enhanced fetch error:", e);
    } finally {
      setEnhancedLoading((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  const enhancedFetchRef = useRef<string>("");
  useEffect(() => {
    // Load enhanced data for ALL selected webinars (not just primary)
    for (const id of selectedIds) {
      const traffic = selectedIds.length > 1 ? (cardTraffic[id] ?? trafficFilter) : trafficFilter;
      // Segment filters only apply in single-view (not compare mode)
      const src = selectedIds.length === 1 ? segmentSource : "";
      const med = selectedIds.length === 1 ? segmentMedium : "";
      const camp = selectedIds.length === 1 ? segmentCampaign : "";
      const cont = selectedIds.length === 1 ? segmentContent : "";
      const investLevels = selectedIds.length === 1 ? selectedInvestmentLevels : [];
      const investKey = investLevels.join("|");
      const key = `${id}-${traffic}-${appTypeFilter}-${src}-${med}-${camp}-${cont}-${investKey}`;
      if (!enhancedData[key] && !enhancedLoading.has(id)) {
        loadEnhanced(id, traffic, appTypeFilter, src, med, camp, cont, investLevels);
      }
    }
    // Track primary for ref-based dedup on filter changes
    const investKey = selectedInvestmentLevels.join("|");
    const key = `${primaryId}-${trafficFilter}-${appTypeFilter}-${segmentSource}-${segmentMedium}-${segmentCampaign}-${segmentContent}-${investKey}`;
    if (primaryId && key !== enhancedFetchRef.current) {
      enhancedFetchRef.current = key;
    }
  }, [selectedIds, primaryId, trafficFilter, appTypeFilter, segmentSource, segmentMedium, segmentCampaign, segmentContent, selectedInvestmentLevels, cardTraffic, loadEnhanced, enhancedData, enhancedLoading]);

  // The Sheets view always compares the selected webinar with its three predecessors, using
  // unsegmented all-traffic webinar cohorts so every column has identical semantics.
  useEffect(() => {
    if (viewMode !== "list") return;
    for (const id of sheetComparisonIds) {
      if (!funnelCache[id] && !loadingIds.has(id)) loadWebinar(id);
      const key = `${id}-all-all-----`;
      if (!enhancedData[key] && !enhancedLoading.has(id)) loadEnhanced(id, "all", "all");
    }
  }, [viewMode, sheetComparisonIds, funnelCache, loadingIds, enhancedData, enhancedLoading, loadWebinar, loadEnhanced]);

  const savePitchMinute = async (webinarId: string) => {
    const val = parseInt(pitchMinuteInput[webinarId] ?? "");
    if (isNaN(val) || val < 0) return;
    setSavingPitchMinute(webinarId);
    try {
      await fetch(`/api/webinar2/pitch-minute/${webinarId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pitchMinute: val }),
      });
      loadEnhanced(webinarId, trafficFilter, appTypeFilter, segmentSource, segmentMedium, segmentCampaign, segmentContent, selectedInvestmentLevels);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingPitchMinute(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleStageClick = (webinarId: string, label: string, traffic: "all" | "paid" | "organic" = "all") => {
    // Full Apps → show investment breakdown modal
    if (label === "Full Apps") {
      const f = funnelCache[webinarId];
      if (f) {
        setInvestmentModal({
          webinarName: f.webinarName ?? webinarId,
          breakdown: f.investmentBreakdown ?? {},
          qualifiedApps: f.qualifiedApps ?? 0,
          totalFullApps: f.fullApps ?? 0,
        });
      }
      return;
    }
    const stageKey = STAGE_KEY_MAP[label];
    if (!stageKey) return;
    setStageLeadsModal({ webinarId, label, stageKey, traffic });
  };

  const handleUploadSuccess = (webinarId: string) => {
    setUploadModal(null);
    setFunnelCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    setRetentionCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    setCashBySourceCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    loadWebinar(webinarId);
  };

  const handleAddWebinarSuccess = (newId: string) => {
    setAddWebinarModal(false);
    fetch("/api/webinar2/list")
      .then((r) => r.json())
      .then((d: { success: boolean; webinars: WebinarDef[] }) => {
        if (d.success) {
          const sorted = [...d.webinars].reverse();
          setWebinars(sorted);
          setSelectedIds([newId]);
        }
      })
      .catch(console.error);
  };

  const saveAdSpend = async (webinarId: string) => {
    const val = parseFloat(adSpendInput[webinarId] ?? "");
    if (isNaN(val) || val < 0) return;
    setSavingAdSpend(webinarId);
    try {
      await fetch(`/api/webinar2/ad-spend/${webinarId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adSpend: val }),
      });
      setFunnelCache((prev) => {
        const f = prev[webinarId];
        if (!f) return prev;
        const roas = val > 0 ? f.paidCash / val : 0;
        return { ...prev, [webinarId]: { ...f, adSpend: val, roas } };
      });
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAdSpend(null);
    }
  };

  const saveEngagementMetrics = async (webinarId: string) => {
    const cal = parseInt(calendarInput[webinarId] ?? "");
    const wa = parseInt(whatsappInput[webinarId] ?? "");
    if (isNaN(cal) && isNaN(wa)) return;
    setSavingEngagement(webinarId);
    try {
      const body: Record<string, number> = {};
      if (!isNaN(cal) && cal >= 0) body.addToCalendar = cal;
      if (!isNaN(wa) && wa >= 0) body.joinedWhatsApp = wa;
      await fetch(`/api/webinar2/engagement-metrics/${webinarId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      // Invalidate enhanced cache so cards refresh
      setEnhancedData((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (key.startsWith(webinarId + "-")) delete next[key];
        }
        return next;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setSavingEngagement(null);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const isSingleView = selectedIds.length <= 1;
  const primaryFunnel = primaryId ? funnelCache[primaryId] ?? null : null;
  const primaryRetention = primaryId ? retentionCache[primaryId] ?? [] : [];
  const primaryRetentionBySource = primaryId ? retentionBySourceCache[primaryId] ?? [] : [];
  const primaryCashBySource = primaryId ? cashBySourceCache[primaryId] ?? [] : [];
  const primaryLoading = primaryId ? loadingIds.has(primaryId) : false;

  const funnelStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.totalOptins },
        ...(primaryFunnel.hasZoomData
          ? [
              { label: "Attended", value: primaryFunnel.totalAttended, subLabel: `${Math.round(primaryFunnel.avgDuration)}m avg` },
            ]
          : []),
        { label: "Applications", value: primaryFunnel.totalApps },
        { label: "Calls Booked", value: primaryFunnel.totalCalls },
        { label: "Calls Taken", value: primaryFunnel.totalShowed, subLabel: `${Math.round(primaryFunnel.callShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.totalWins, subLabel: `${primaryFunnel.callToCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.totalCash, isCash: true },
      ]
    : [];

  const organicCloseRate = primaryFunnel && primaryFunnel.organicShowed > 0
    ? (primaryFunnel.organicWins / primaryFunnel.organicShowed) * 100 : 0;
  const paidCloseRate = primaryFunnel && primaryFunnel.paidShowed > 0
    ? (primaryFunnel.paidWins / primaryFunnel.paidShowed) * 100 : 0;
  const organicShowRate = primaryFunnel && primaryFunnel.organicCalls > 0
    ? (primaryFunnel.organicShowed / primaryFunnel.organicCalls) * 100 : 0;
  const paidShowRate = primaryFunnel && primaryFunnel.paidCalls > 0
    ? (primaryFunnel.paidShowed / primaryFunnel.paidCalls) * 100 : 0;

  const organicStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.organicOptins },
        { label: "Applications", value: primaryFunnel.organicApps },
        { label: "Calls Booked", value: primaryFunnel.organicCalls },
        { label: "Calls Taken", value: primaryFunnel.organicShowed, subLabel: `${Math.round(organicShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.organicWins, subLabel: `${organicCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.organicCash, isCash: true },
      ]
    : [];

  const paidStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.paidOptins },
        { label: "Applications", value: primaryFunnel.paidApps },
        { label: "Calls Booked", value: primaryFunnel.paidCalls },
        { label: "Calls Taken", value: primaryFunnel.paidShowed, subLabel: `${Math.round(paidShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.paidWins, subLabel: `${paidCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.paidCash, isCash: true },
      ]
    : [];

  // ── Helpers for multi-card funnels ─────────────────────────────────────────
  const getCardStages = (f: WebinarFunnel, traffic: "all" | "paid" | "organic"): FunnelStage[] => {
    if (traffic === "organic") {
      const sr = f.organicCalls > 0 ? (f.organicShowed / f.organicCalls) * 100 : 0;
      const cr = f.organicShowed > 0 ? (f.organicWins / f.organicShowed) * 100 : 0;
      return [
        { label: "Opt-ins", value: f.organicOptins },
        { label: "Applications", value: f.organicApps },
        { label: "Calls Booked", value: f.organicCalls },
        { label: "Calls Taken", value: f.organicShowed, subLabel: `${Math.round(sr)}% show rate` },
        { label: "Closed", value: f.organicWins, subLabel: `${cr.toFixed(1)}% close rate` },
        { label: "Cash", value: f.organicCash, isCash: true },
      ];
    }
    if (traffic === "paid") {
      const sr = f.paidCalls > 0 ? (f.paidShowed / f.paidCalls) * 100 : 0;
      const cr = f.paidShowed > 0 ? (f.paidWins / f.paidShowed) * 100 : 0;
      return [
        { label: "Opt-ins", value: f.paidOptins },
        { label: "Applications", value: f.paidApps },
        { label: "Calls Booked", value: f.paidCalls },
        { label: "Calls Taken", value: f.paidShowed, subLabel: `${Math.round(sr)}% show rate` },
        { label: "Closed", value: f.paidWins, subLabel: `${cr.toFixed(1)}% close rate` },
        { label: "Cash", value: f.paidCash, isCash: true },
      ];
    }
    const sr = f.totalCalls > 0 ? (f.totalShowed / f.totalCalls) * 100 : 0;
    const cr = f.totalShowed > 0 ? (f.totalWins / f.totalShowed) * 100 : 0;
    return [
      { label: "Opt-ins", value: f.totalOptins },
      { label: "Applications", value: f.totalApps },
      { label: "Calls Booked", value: f.totalCalls },
      { label: "Calls Taken", value: f.totalShowed, subLabel: `${Math.round(sr)}% show rate` },
      { label: "Closed", value: f.totalWins, subLabel: `${cr.toFixed(1)}% close rate` },
      { label: "Cash", value: f.totalCash, isCash: true },
    ];
  };

  // Enhanced data for current selection
  const enhancedKey = primaryId ? `${primaryId}-${trafficFilter}-${appTypeFilter}-${segmentSource}-${segmentMedium}-${segmentCampaign}-${segmentContent}-${selectedInvestmentLevels.join("|")}` : "";
  const enhanced = enhancedKey ? enhancedData[enhancedKey] ?? null : null;
  const enhancedIsLoading = primaryId ? enhancedLoading.has(primaryId) : false;

  // ── Render ─────────────────────────────────────────────────────────────────

  // Helper to render a webinar's content (used in both single and compare views)
  const renderWebinarContent = (id: string, isCompare = false, showHighlights = true) => {
    const f = funnelCache[id];
    const isLoading = loadingIds.has(id);
    const def = webinars.find((w) => w.id === id);
    const traffic = isCompare ? (cardTraffic[id] ?? "all") : trafficFilter;
    const eKey = isCompare
      ? `${id}-${traffic}-${appTypeFilter}-----`
      : `${id}-${traffic}-${appTypeFilter}-${segmentSource}-${segmentMedium}-${segmentCampaign}-${segmentContent}-${selectedInvestmentLevels.join("|")}`;
    const enh = enhancedData[eKey] ?? null;
    const enhLoading = enhancedLoading.has(id);
    const ret = retentionCache[id] ?? [];
    const retBySrc = retentionBySourceCache[id] ?? [];
    const cashBySrc = cashGroupBy === "creator" ? (cashByCreatorCache[id] ?? []) : (cashBySourceCache[id] ?? []);
    const creatorDistributions = creatorDistributionsCache[id] ?? null;
    const timingKey = metricsView === "on-webby" ? "onWebby" : metricsView === "post-webby" ? "postWebby" : "total";
    const applicationCreatorData = creatorDistributions?.applications[applicationPieType][timingKey] ?? [];
    const bookingCreatorData = creatorDistributions?.bookedCalls[timingKey] ?? [];
    const optinsByUtm = optinsByUtmCache[id] ?? null;
    const attendeesByCreator = attendeesByCreatorCache[id] ?? [];
    const retSources = retBySrc.length > 0 ? Object.keys(retBySrc[0]).filter((k) => k !== "minute") : [];
    const srcTotals = sourceTotalsCache[id] ?? {};
    const pitchMin = enh?.onWebinar?.pitchMinute ?? null;
    const regTimingPoints = regTimingCache[id] ?? [];
    const sortedSelectedByDate = selectedIds
      .map((sid) => webinars.find((w) => w.id === sid))
      .filter((w): w is WebinarDef => Boolean(w))
      .sort((a, b) => a.date.localeCompare(b.date));
    const prevCompareDef = isCompare
      ? sortedSelectedByDate[sortedSelectedByDate.findIndex((w) => w.id === id) - 1] ?? null
      : null;
    const prevCompareId = prevCompareDef?.id ?? null;
    const prevTraffic = prevCompareId ? (cardTraffic[prevCompareId] ?? trafficFilter) : traffic;
    const prevF = prevCompareId ? funnelCache[prevCompareId] ?? null : null;
    const prevEnh = prevCompareId ? enhancedData[`${prevCompareId}-${prevTraffic}-${appTypeFilter}-----`] ?? null : null;

    const renderMetricDelta = (current?: number | null, previous?: number | null) => {
      if (!isCompare || !prevCompareId) return null;
      const delta = calcMetricDelta(current, previous);
      if (!delta) return null;
      const tone = delta.direction === "up" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
        : delta.direction === "down" ? "text-red-400 bg-red-500/10 border-red-500/20"
        : "text-muted-foreground bg-muted/30 border-border/50";
      const arrow = delta.direction === "up" ? "↗" : delta.direction === "down" ? "↘" : "→";
      return (
        <div className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded border text-[9px] font-semibold ${tone}`} title={`Compared to ${prevCompareDef?.name ?? prevCompareId}`}>
          <span>{arrow}</span><span>{formatMetricDelta(delta)}</span>
        </div>
      );
    };

    const aud = def?.audienceType ?? "cold";

    const resolveTarget = (label: string, rawValue: number | undefined, targets: MetricTargetMap): { rangeStr: string; indicator: "above" | "in" | "below" } | null =>
      resolveWebinarTarget(label, rawValue, targets, aud, activeBlock?.optins ?? 0);

    const makeTrendIcon = (indicator: "above" | "in" | "below", color: string) =>
      indicator === "above" ? (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="2,11 5.5,7 8.5,9 12,3" /><polyline points="9,3 12,3 12,6" />
        </svg>
      ) : indicator === "below" ? (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="2,3 5.5,7 8.5,5 12,11" /><polyline points="9,11 12,11 12,8" />
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="7" cy="7" r="5.5" /><polyline points="4.5,7.5 6.5,9.5 9.5,5" />
        </svg>
      );

    const renderTargetBadge = (label: string, rawValue: number | undefined, targets: MetricTargetMap) => {
      if (isCompare) return null;
      const info = resolveTarget(label, rawValue, targets);
      if (!info) return null;
      const { rangeStr, indicator } = info;
      const iconColor = indicator === "above" ? "text-emerald-400" : indicator === "below" ? "text-red-400" : "text-yellow-400";
      return (
        <div className="flex items-center gap-1.5 mt-2">
          {makeTrendIcon(indicator, indicator === "above" ? "#34d399" : indicator === "below" ? "#f87171" : "#facc15")}
          <span className={`text-[10px] font-semibold leading-tight ${iconColor}`}>target {rangeStr}</span>
        </div>
      );
    };

    if (isLoading) return <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">Loading…</div>;
    if (!f) return <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">No data</div>;

    // Build stage data for funnel
    const stages = isCompare ? getCardStages(f, traffic) : (traffic === "all" ? funnelStages : traffic === "organic" ? organicStages : paidStages);

    // Pick which sales block to show based on metricsView toggle
    const activeBlock = enh
      ? (metricsView === "on-webby" ? enh.salesOnWebby : metricsView === "post-webby" ? enh.salesPostWebby : enh.salesTotal)
      : null;
    const totalBlock = enh?.salesTotal ?? null;
    const onBlock = enh?.salesOnWebby ?? null;
    const postBlock = enh?.salesPostWebby ?? null;
    const prevActiveBlock = prevEnh
      ? (metricsView === "on-webby" ? prevEnh.salesOnWebby : metricsView === "post-webby" ? prevEnh.salesPostWebby : prevEnh.salesTotal)
      : null;
    const prevTotalBlock = prevEnh?.salesTotal ?? null;
    const prevOnBlock = prevEnh?.salesOnWebby ?? null;
    const prevPostBlock = prevEnh?.salesPostWebby ?? null;

    // Build KPIs from active block (enhanced) or fallback to funnel data
    type KpiCard = { l: string; v: string; rawValue?: number; subCount?: string; onVal?: number; postVal?: number; totalVal?: number; isRate?: boolean; isCash?: boolean; clickable?: boolean };
    // For Calls Taken: use all-EOC count in Total view (fixes 9→10 discrepancy), cohort-matched in on/post-webby.
    // Keep Calls Due in every perspective so the cards always retain Total view's grid positions.
    // In a cohort, due means taken + no-show EOCs: reschedules are not due yet and cancelled calls never were
    // (cancellations come from booking status — Joey, Oct 19 2026).
    const takenCallsVal = enh && metricsView === "total" ? enh.totalEocsTaken : activeBlock?.takenCalls ?? 0;
    const prevTakenCallsVal = prevEnh && metricsView === "total" ? prevEnh.totalEocsTaken : prevActiveBlock?.takenCalls ?? null;
    const callsDueVal = enh && metricsView === "total"
      ? enh.totalEocsDue
      : (activeBlock?.takenCalls ?? 0) + (activeBlock?.noShows ?? 0);
    // Cancelled Calls = appointments whose latest Call Bookings status is cancelled (the Book a call rule).
    const cancelledOf = (block: SalesMetricsBlock | null | undefined) => block ? (block.cancelledCalls ?? (block.cancelledBySales + block.cancelledByLead)) : 0;

    const salesKpis: KpiCard[] = activeBlock && totalBlock && onBlock && postBlock ? [
      { l: "Opt-ins",       v: fmtN(activeBlock.optins),       totalVal: totalBlock.optins,       onVal: onBlock.optins,       postVal: postBlock.optins, clickable: true },
      ...(isCompare || f.landingPageVisitors > 0 ? [{ l: "Landing Page Visitors", v: fmtN(f.landingPageVisitors), isRate: false }] : []),
      ...(isCompare || f.optinRate > 0 ? [{ l: "Opt-in Rate", v: fmtPct(f.optinRate), isRate: true, subCount: f.landingPageVisitors > 0 ? `${fmtN(activeBlock.optins)} / ${fmtN(f.landingPageVisitors)}` : undefined }] : []),
      { l: "Total Apps",          v: fmtN(activeBlock.apps),         totalVal: totalBlock.apps,         onVal: onBlock.apps,         postVal: postBlock.apps, clickable: true },
      { l: "Full Apps",           v: fmtN(activeBlock.fullApps),     totalVal: totalBlock.fullApps,     onVal: onBlock.fullApps,     postVal: postBlock.fullApps, clickable: true },
      { l: "Qualified Apps", v: fmtN(activeBlock.qualifiedApps), totalVal: totalBlock.qualifiedApps, onVal: onBlock.qualifiedApps, postVal: postBlock.qualifiedApps },
      { l: "Partial Apps",  v: fmtN(activeBlock.partialApps),  totalVal: totalBlock.partialApps,  onVal: onBlock.partialApps,  postVal: postBlock.partialApps, clickable: true },
      { l: "Webinar Bookings", v: fmtN(activeBlock.bookedCalls), clickable: true },
      { l: "End of Call Reports Filled", v: fmtN(enh.totalEocReports) },
      { l: "Calls Taken",   v: fmtN(takenCallsVal),            totalVal: totalBlock.takenCalls,   onVal: onBlock.takenCalls,   postVal: postBlock.takenCalls, clickable: true },
      { l: "Show Rate",
        v: enh && metricsView === "total"
          ? fmtPct(enh.totalEocsDue > 0 ? (enh.totalEocsTaken / enh.totalEocsDue) * 100 : 0)
          : fmtPct(activeBlock.showRate),
        // Taken ÷ calls due (taken + no-shows); cancelled and rescheduled calls never enter it (Joey, Oct 19 2026).
        subCount: enh && metricsView === "total"
          ? `${fmtN(enh.totalEocsTaken)} taken / ${fmtN(enh.totalEocsDue)} due`
          : `${fmtN(activeBlock.takenCalls)} taken / ${fmtN(activeBlock.takenCalls + activeBlock.noShows)} due`,
        isRate: true },
      ...(isCompare || totalBlock.noShows > 0 ? [{ l: "No Shows", v: fmtN(activeBlock.noShows), totalVal: totalBlock.noShows, onVal: onBlock.noShows, postVal: postBlock.noShows }] : []),
      ...(isCompare || cancelledOf(totalBlock) > 0 ? [{
        l: "Cancelled Calls",
        // Count plus its % of this view's webinar bookings (Joey, Oct 19, 2026: cancellations always show "N (X%)").
        v: `${fmtN(cancelledOf(activeBlock))} (${activeBlock.bookedCalls > 0 ? `${Math.round(cancelledOf(activeBlock) / activeBlock.bookedCalls * 100)}%` : "—"})`,
        subCount: `of ${fmtN(activeBlock.bookedCalls)} webinar bookings`,
        totalVal: cancelledOf(totalBlock),
        onVal: cancelledOf(onBlock),
        postVal: cancelledOf(postBlock),
        isCancelCard: true,
      }] : []),
      { l: "Closes",        v: fmtN(activeBlock.closed),       totalVal: totalBlock.closed,       onVal: onBlock.closed,       postVal: postBlock.closed, clickable: true },
      ...(isCompare || activeBlock.deposits > 0 ? [{ l: "Deposits", v: fmtN(activeBlock.deposits), totalVal: totalBlock.deposits, onVal: onBlock.deposits, postVal: postBlock.deposits }] : []),
      { l: "Close Rate",    v: fmtPct(activeBlock.closeRate),  isRate: true },
      { l: "Cash",          v: fmt$(activeBlock.cash),         totalVal: totalBlock.cash,         onVal: onBlock.cash,         postVal: postBlock.cash, isCash: true, clickable: true },
      { l: "Cash from Opt-ins", v: fmt$(activeBlock.cashFromOptins), totalVal: totalBlock.cashFromOptins, onVal: onBlock.cashFromOptins, postVal: postBlock.cashFromOptins, isCash: true },
      { l: "Cash from Applications", v: fmt$(activeBlock.cashFromApplications), totalVal: totalBlock.cashFromApplications, onVal: onBlock.cashFromApplications, postVal: postBlock.cashFromApplications, isCash: true },
      { l: "Cash from Booked Calls", v: fmt$(activeBlock.cashFromBookedCalls), totalVal: totalBlock.cashFromBookedCalls, onVal: onBlock.cashFromBookedCalls, postVal: postBlock.cashFromBookedCalls, isCash: true },
      { l: "AOV",           v: activeBlock.auv > 0 ? fmt$(activeBlock.auv) : "—", isRate: true },
      { l: "Cash/Call",     v: activeBlock.cashPerCall > 0 ? fmt$(activeBlock.cashPerCall) : "—", isRate: true },
      ...(isCompare || totalBlock.costPerBooking > 0 ? [{ l: "Cost/Booking", v: metricsView === "total" ? fmt$(totalBlock.costPerBooking) : "—", isRate: true }] : []),
      { l: "Full App→Booking", v: fmtPct(activeBlock.appToBookingRate), subCount: `${fmtN(activeBlock.fullApps)} / ${fmtN(activeBlock.bookedCalls)}`, isRate: true },
      { l: "Optin→Booking",    v: fmtPct(activeBlock.optins > 0 ? (activeBlock.bookedCalls / activeBlock.optins) * 100 : 0), subCount: `${fmtN(activeBlock.optins)} / ${fmtN(activeBlock.bookedCalls)}`, isRate: true },
      { l: "Opt-in→Full App",  v: fmtPct(activeBlock.optins > 0 ? (activeBlock.fullApps / activeBlock.optins) * 100 : 0), subCount: `${fmtN(activeBlock.optins)} / ${fmtN(activeBlock.fullApps)}`, isRate: true },
      { l: "Optin→Qual App",   v: fmtPct(activeBlock.optins > 0 ? (activeBlock.qualifiedApps / activeBlock.optins) * 100 : 0), subCount: `${fmtN(activeBlock.optins)} / ${fmtN(activeBlock.qualifiedApps)}`, isRate: true },
      { l: "Qual→Booking",     v: fmtPct(activeBlock.qualifiedToBookingRate), subCount: `${fmtN(activeBlock.qualifiedBooked)} / ${fmtN(activeBlock.qualifiedApps)} qual`, isRate: true },
      {
        l: "Add to Calendar",
        v: enh.addToCalendar > 0 ? fmtPct((enh.addToCalendar / Math.max(activeBlock.optins, 1)) * 100) : "—",
        subCount: `${fmtN(enh.addToCalendar)} / ${fmtN(activeBlock.optins)}`,
        isRate: true,
      },
      {
        l: "Joined WhatsApp",
        v: enh.joinedWhatsApp > 0 ? fmtPct((enh.joinedWhatsApp / Math.max(activeBlock.optins, 1)) * 100) : "—",
        subCount: `${fmtN(enh.joinedWhatsApp)} / ${fmtN(activeBlock.optins)}`,
        isRate: true,
      },
      ...(enh.adSpend > 0 ? [
        { l: "Ad Spend", v: fmt$(enh.adSpend), isRate: true },
        { l: "ROAS", v: activeBlock.roas > 0 ? `${activeBlock.roas.toFixed(1)}x` : "—", isRate: true },
      ] : []),
      ...(isCompare || totalBlock.chargebackRate > 0 ? [{ l: "Chargeback %", v: metricsView === "total" ? fmtPct(totalBlock.chargebackRate) : "—", isRate: true }] : []),
    ] : [
      { l: "Opt-ins",       v: fmtN(traffic === "paid" ? f.paidOptins : traffic === "organic" ? f.organicOptins : f.totalOptins), clickable: true },
      ...(f.landingPageVisitors > 0 ? [{ l: "Landing Page Visitors", v: fmtN(f.landingPageVisitors), isRate: false }] : []),
      ...(f.optinRate > 0 ? [{ l: "Opt-in Rate", v: fmtPct(f.optinRate), isRate: true, subCount: f.landingPageVisitors > 0 ? `${fmtN(f.totalOptins)} / ${fmtN(f.landingPageVisitors)}` : undefined }] : []),
      { l: "Full Apps",     v: fmtN(traffic === "paid" ? f.paidApps : traffic === "organic" ? f.organicApps : f.totalApps), clickable: true },
      { l: "Calls Booked",  v: fmtN(traffic === "paid" ? f.paidCalls : traffic === "organic" ? f.organicCalls : f.totalCalls), clickable: true },
      { l: "Calls Taken",   v: fmtN(traffic === "paid" ? f.paidShowed : traffic === "organic" ? f.organicShowed : f.totalShowed), clickable: true },
      { l: "Show Rate",     v: fmtPct(f.callShowRate), isRate: true },
      { l: "Closes",        v: fmtN(traffic === "paid" ? f.paidWins : traffic === "organic" ? f.organicWins : f.totalWins), clickable: true },
      { l: "Close Rate",    v: fmtPct(f.callToCloseRate), isRate: true },
      { l: "Cash",          v: fmt$(traffic === "paid" ? f.paidCash : traffic === "organic" ? f.organicCash : f.totalCash), clickable: true },
      ...(f.adSpend > 0 ? [
        { l: "Ad Spend", v: fmt$(f.adSpend), isRate: true },
        { l: "ROAS", v: f.roas > 0 ? `${f.roas.toFixed(1)}x` : "—", isRate: true },
      ] : []),
    ];

    const salesRawValue = (label: string, block: SalesMetricsBlock | null, total: SalesMetricsBlock | null, sourceF: WebinarFunnel | null, sourceEnh: EnhancedWebinarData | null) => {
      if (!block) {
        if (!sourceF) return undefined;
        const t = sourceF === f ? traffic : prevTraffic;
        if (label === "Opt-ins") return t === "paid" ? sourceF.paidOptins : t === "organic" ? sourceF.organicOptins : sourceF.totalOptins;
        if (label === "Landing Page Visitors") return sourceF.landingPageVisitors;
        if (label === "Opt-in Rate") return sourceF.optinRate;
        if (label === "Full Apps") return t === "paid" ? sourceF.paidApps : t === "organic" ? sourceF.organicApps : sourceF.totalApps;
        if (label === "Calls Booked") return t === "paid" ? sourceF.paidCalls : t === "organic" ? sourceF.organicCalls : sourceF.totalCalls;
        if (label === "Calls Taken") return t === "paid" ? sourceF.paidShowed : t === "organic" ? sourceF.organicShowed : sourceF.totalShowed;
        if (label === "Show Rate") return sourceF.callShowRate;
        if (label === "Closes") return t === "paid" ? sourceF.paidWins : t === "organic" ? sourceF.organicWins : sourceF.totalWins;
        if (label === "Close Rate") return sourceF.callToCloseRate;
        if (label === "Cash") return t === "paid" ? sourceF.paidCash : t === "organic" ? sourceF.organicCash : sourceF.totalCash;
        if (label === "Ad Spend") return sourceF.adSpend;
        if (label === "ROAS") return sourceF.roas;
        return undefined;
      }
      if (label === "Opt-ins") return block.optins;
      if (label === "Landing Page Visitors") return sourceF?.landingPageVisitors;
      if (label === "Opt-in Rate") return sourceF?.optinRate;
      if (label === "Total Apps") return block.apps;
      if (label === "Full Apps") return block.fullApps;
      if (label === "Qualified Apps") return block.qualifiedApps;
      if (label === "Partial Apps") return block.partialApps;
      if (label === "Calls Booked" || label === "Webinar Bookings") return block.bookedCalls;
      if (label === "End of Call Reports Filled") return sourceEnh?.totalEocReports;
      if (label === "Calls Taken") return sourceEnh && metricsView === "total" ? sourceEnh.totalEocsTaken : block.takenCalls;
      if (label === "Show Rate") return sourceEnh && metricsView === "total" ? (sourceEnh.totalEocsDue > 0 ? (sourceEnh.totalEocsTaken / sourceEnh.totalEocsDue) * 100 : 0) : block.showRate;
      if (label === "No Shows") return block.noShows;
      if (label === "Cancelled Calls") return block.cancelledCalls ?? (block.cancelledBySales + block.cancelledByLead);
      if (label === "Closes") return block.closed;
      if (label === "Deposits") return block.deposits;
      if (label === "Close Rate") return block.closeRate;
      if (label === "Cash") return block.cash;
      if (label === "Cash from Opt-ins") return block.cashFromOptins;
      if (label === "Cash from Applications") return block.cashFromApplications;
      if (label === "Cash from Booked Calls") return block.cashFromBookedCalls;
      if (label === "AOV") return block.auv;
      if (label === "Cash/Call") return block.cashPerCall;
      if (label === "Cost/Booking") return metricsView === "total" ? total?.costPerBooking : undefined;
      if (label === "Full App→Booking") return block.appToBookingRate;
      if (label === "Optin→Booking") return block.optins > 0 ? (block.bookedCalls / block.optins) * 100 : 0;
      if (label === "Opt-in→Full App") return block.optins > 0 ? (block.fullApps / block.optins) * 100 : 0;
      if (label === "Optin→Qual App") return block.optins > 0 ? (block.qualifiedApps / block.optins) * 100 : 0;
      if (label === "Qual→Booking") return block.qualifiedToBookingRate;
      if (label === "Add to Calendar") return sourceEnh ? (sourceEnh.addToCalendar / Math.max(block.optins, 1)) * 100 : undefined;
      if (label === "Joined WhatsApp") return sourceEnh ? (sourceEnh.joinedWhatsApp / Math.max(block.optins, 1)) * 100 : undefined;
      if (label === "Ad Spend") return sourceEnh?.adSpend;
      if (label === "ROAS") return block.roas;
      if (label === "Chargeback %") return metricsView === "total" ? total?.chargebackRate : undefined;
      return undefined;
    };

    const salesKpisWithDeltas = salesKpis.map((k) => ({
      ...k,
      rawValue: salesRawValue(k.l, activeBlock, totalBlock, f, enh),
      prevRawValue: salesRawValue(k.l, prevActiveBlock, prevTotalBlock, prevF, prevEnh),
    }));

    // ── List view early return ──────────────────────────────────────────────
    if (viewMode === "list" && !isCompare) {
      const comparisonColumns = sheetComparisonIds.map((webinarId) => ({ id: webinarId, def: webinars.find((webinar) => webinar.id === webinarId), funnel: funnelCache[webinarId], enhanced: enhancedData[`${webinarId}-all-all-----`] }));
      if (comparisonColumns.some((column) => !column.funnel || !column.enhanced)) return <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">Loading selected webinar and up to three preceding webinars…</div>;
      const fmtSheet = (value: number | null, kind: "count" | "money" | "rate" = "count") => value === null || value === undefined || !Number.isFinite(value) ? "—" : kind === "money" ? fmt$(value) : kind === "rate" ? fmtPct(value) : fmtN(value);
      const sheetValue = (metric: string, column: { funnel?: WebinarFunnel; enhanced?: EnhancedWebinarData }): [number | null, "count" | "money" | "rate"] => {
        const sf = column.funnel!; const se = column.enhanced!; const sales = se.salesTotal; const booked = sales.bookedCalls; const closed = sales.closed + sales.deposits; const shown = se.totalEocsTaken; const due = se.totalEocsDue;
        const rateOf = (top: number, bottom: number) => bottom > 0 ? top / bottom * 100 : null;
        const values: Record<string, [number | null, "count" | "money" | "rate"]> = {
          "Opt-ins": [sales.optins, "count"], "Landing Page Visitors": [sf.landingPageVisitors || null, "count"], "Opt-in Rate": [sf.optinRate || null, "rate"], "Applications": [sales.apps, "count"], "Leads": [sales.apps, "count"], "Full Applications": [sales.fullApps, "count"], "Partial Applications": [sales.partialApps, "count"], "Qualified Applications": [sales.qualifiedApps, "count"], "Booked Calls": [booked, "count"], "Total Booked Closing Calls (All)": [booked, "count"], "EOCs": [due, "count"], "Showed Calls": [shown, "count"], "Total Closing Calls Completed": [shown, "count"], "Closed Deals": [closed, "count"], "Deals Closed": [closed, "count"], "Cash Collected": [sales.cash, "money"],
          "Lead to Booking % (ALL)": [rateOf(booked, sales.apps), "rate"], "Closing Show %": [rateOf(shown, due), "rate"], "Closing %": [rateOf(closed, shown), "rate"], "Lead to Close %": [rateOf(closed, sales.apps), "rate"], "Closing Call Booked To Close %": [rateOf(closed, booked), "rate"], "Average Cash per Application": [sales.apps ? sales.cash / sales.apps : null, "money"], "Average Cash per Booked Call": [booked ? sales.cash / booked : null, "money"], "Avg Cash / Call": [shown ? sales.cash / shown : null, "money"],
          "On-Webinar Attended": [se.onWebinar.attended, "count"], "On-Webinar Show Rate": [se.onWebinar.showRate, "rate"], "At Pitch": [se.onWebinar.attendeesAtPitch || null, "count"], "On-Webinar Booking Rate": [se.onWebinar.attendeesAtPitch ? rateOf(booked, se.onWebinar.attendeesAtPitch) : null, "rate"], "Total App Rate at Pitch": [se.onWebinar.attendeesAtPitch ? rateOf(sales.apps, se.onWebinar.attendeesAtPitch) : null, "rate"], "Full App Rate at Pitch": [se.onWebinar.attendeesAtPitch ? rateOf(sales.fullApps, se.onWebinar.attendeesAtPitch) : null, "rate"], "Add to Calendar": [se.addToCalendar || null, "count"], "Joined WhatsApp": [se.joinedWhatsApp || null, "count"],
        };
        return values[metric] ?? [null, "count"];
      };
      const comparisonGroups: Array<[string, string[]]> = [
        ["Webinar funnel", ["Opt-ins", "Landing Page Visitors", "Opt-in Rate", "Applications", "Full Applications", "Partial Applications", "Qualified Applications", "Booked Calls", "EOCs", "Showed Calls", "Closed Deals", "Cash Collected"]],
        ["Sales activity", ["Leads", "Bad Data / Junk", "Outbound Calls", "Connected", "Total Booked Closing Calls (IB)", "Total Booked Closing Calls (OB)", "Total Booked Closing Calls (All)", "Total Closing Calls Completed", "Offers Made", "Deals Closed"]],
        ["Velocity metrics", ["Bad Data %", "Connect To Set Avg", "Avg OB Touch Density", "Connect Rate %", "Lead to Booking % (OB)", "Lead to Booking % (IB)", "Lead to Booking % (ALL)", "Closing Show %", "Offer %", "Closing %"]],
        ["Performance metrics", ["Lead to Close %", "Closing Call Booked To Close %", "Offer To Close %", "Average Cash per Application", "Average Cash per Booked Call", "Avg Cash / Call"]],
        ["Webinar-specific", ["On-Webinar Attended", "On-Webinar Show Rate", "At Pitch", "On-Webinar Booking Rate", "Total App Rate at Pitch", "Full App Rate at Pitch", "Add to Calendar", "Joined WhatsApp"]],
      ];
      return <div className="space-y-3"><div><h2 className="text-sm font-bold text-foreground">Webinar Velocity Metrics</h2><p className="text-[11px] text-muted-foreground">Selected webinar plus the three immediately preceding webinars. Each column uses that webinar’s own attributed cohort; a dash is unavailable, not zero.</p></div><div className="overflow-x-auto rounded-lg" style={{ border: "1px solid #e5e7eb", backgroundColor: "#ffffff" }} data-webinar-velocity-table><table className="min-w-[980px] w-full border-collapse text-xs" style={{ backgroundColor: "#ffffff", color: "#111827" }}><thead><tr style={{ backgroundColor: "#ffffff", borderBottom: "2px solid #e5e7eb" }}><th className="sticky left-0 z-10 min-w-[260px] px-3 py-2 text-left font-semibold" style={{ backgroundColor: "#ffffff", color: "#111827" }}>Metric</th>{comparisonColumns.map((column) => <th key={column.id} className="min-w-[170px] px-3 py-2 text-right font-semibold" style={{ color: "#111827" }}><div>{column.funnel?.webinarName ?? column.def?.name ?? column.id}</div><div className="mt-0.5 text-[10px] font-normal" style={{ color: "#6b7280" }}>{formatDate(column.funnel?.webinarDate ?? column.def?.date ?? "")}</div></th>)}</tr></thead><tbody>{comparisonGroups.flatMap(([section, metrics]) => [<tr key={`section-${section}`} style={{ backgroundColor: "#ffffff", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}><td colSpan={comparisonColumns.length + 1} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#6b7280" }}>{section}</td></tr>, ...metrics.map((metric) => <tr key={`${section}-${metric}`} style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #f1f5f9" }}><td className="sticky left-0 z-[1] px-3 py-2 font-medium" style={{ backgroundColor: "#ffffff", color: "#111827" }}>{metric}</td>{comparisonColumns.map((column) => { const [value, kind] = sheetValue(metric, column); return <td key={`${column.id}-${metric}`} className="px-3 py-2 text-right font-mono tabular-nums" style={{ color: "#111827" }}>{fmtSheet(value, kind)}</td>; })}</tr>)])}</tbody></table></div></div>;
      // Build flat row list with section headers
      type SheetRow = { section: string; metric: string; value: string; notes: string; target?: string; indicator?: "above" | "in" | "below" };
      const sheetRows: SheetRow[] = [];

      // Webinar Info
      sheetRows.push({ section: "Webinar Info", metric: "Audience Type", value: aud === "warm" ? "Warm Traffic" : "Cold Traffic", notes: "" });
      if (def?.funnel) sheetRows.push({ section: "Webinar Info", metric: "Funnel", value: def.funnel, notes: "" });
      if (def?.platform) sheetRows.push({ section: "Webinar Info", metric: "Platform", value: def.platform, notes: "" });

      // Sales Metrics
      salesKpisWithDeltas.forEach((k) => {
        const tgt = resolveTarget(k.l, k.rawValue, SALES_TARGETS);
        sheetRows.push({ section: "Sales Metrics", metric: k.l, value: k.v, notes: k.subCount ?? "", target: tgt?.rangeStr, indicator: tgt?.indicator });
      });

      // On-Webinar Metrics
      if (enh) {
        const owMetrics: { metric: string; value: string; notes: string; rawValue?: number }[] = [
          { metric: "Opt-ins", value: fmtN(enh.onWebinar.registrants), notes: "" },
          { metric: "CPR", value: enh.onWebinar.cpr > 0 ? fmt$(enh.onWebinar.cpr) : "—", notes: "" },
          { metric: "Attended", value: fmtN(enh.onWebinar.attended), notes: "", rawValue: enh.onWebinar.attended },
          { metric: "Show Rate", value: fmtPct(enh.onWebinar.showRate), notes: `${fmtN(enh.onWebinar.attended)} / ${fmtN(enh.onWebinar.registrants)}`, rawValue: enh.onWebinar.showRate },
          { metric: "Avg Duration", value: `${Math.round(enh.onWebinar.avgDuration)}m`, notes: "", rawValue: enh.onWebinar.avgDuration },
          ...(enh.onWebinar.pitchMinute ? [
            { metric: "Pitch Start", value: `${enh.onWebinar.pitchMinute}m`, notes: "", rawValue: enh.onWebinar.pitchMinute },
            { metric: "@ Pitch", value: fmtPct(enh.onWebinar.retentionAtPitch), notes: fmtN(enh.onWebinar.attendeesAtPitch), rawValue: enh.onWebinar.retentionAtPitch },
            { metric: "Booking Rate", value: fmtPct(enh.onWebinar.attendeesAtPitch > 0 ? (enh.salesTotal.bookedCalls / enh.onWebinar.attendeesAtPitch) * 100 : 0), notes: `${fmtN(enh.onWebinar.attendeesAtPitch)} / ${fmtN(enh.salesTotal.bookedCalls)}`, rawValue: enh.onWebinar.attendeesAtPitch > 0 ? (enh.salesTotal.bookedCalls / enh.onWebinar.attendeesAtPitch) * 100 : 0 },
          ] : [{ metric: "Booking Rate", value: fmtPct(enh.onWebinar.bookingRate), notes: "", rawValue: enh.onWebinar.bookingRate }]),
          ...(enh.onWebinar.attendeesAtPitch > 0 ? [
            { metric: "Total App Rate", value: fmtPct(enh.salesTotal.apps / enh.onWebinar.attendeesAtPitch * 100), notes: `${fmtN(enh.salesTotal.apps)} / ${fmtN(enh.onWebinar.attendeesAtPitch)}`, rawValue: enh.salesTotal.apps / enh.onWebinar.attendeesAtPitch * 100 },
            { metric: "Full App Rate",  value: fmtPct(enh.salesTotal.fullApps / enh.onWebinar.attendeesAtPitch * 100), notes: `${fmtN(enh.salesTotal.fullApps)} / ${fmtN(enh.onWebinar.attendeesAtPitch)}`, rawValue: enh.salesTotal.fullApps / enh.onWebinar.attendeesAtPitch * 100 },
            { metric: "Qual App Rate",  value: fmtPct(enh.salesTotal.qualifiedApps / enh.onWebinar.attendeesAtPitch * 100), notes: `${fmtN(enh.onWebinar.attendeesAtPitch)} / ${fmtN(enh.salesTotal.qualifiedApps)}`, rawValue: enh.salesTotal.qualifiedApps / enh.onWebinar.attendeesAtPitch * 100 },
          ] : []),
          ...(enh.dials.totalDials > 0 ? [
            { metric: "Total Dials", value: fmtN(enh.dials.totalDials), notes: "" },
            { metric: "Pickup Rate", value: fmtPct(enh.dials.pickupRate), notes: "" },
          ] : []),
        ];
        owMetrics.forEach((r) => {
          const tgt = resolveTarget(r.metric, r.rawValue, ON_WEBINAR_TARGETS);
          sheetRows.push({ section: "On-Webinar Metrics", metric: r.metric, value: r.value, notes: r.notes, target: tgt?.rangeStr, indicator: tgt?.indicator });
        });
      }

      // Zoom Attendance
      if (f.hasZoomData) {
        const zoomRows: { metric: string; value: string; notes: string; rawValue?: number }[] = [
          { metric: "Registrants", value: fmtN(f.totalRegistrants), notes: "" },
          { metric: "Attended", value: fmtN(f.totalAttended), notes: "", rawValue: f.totalAttended },
          { metric: "Show Rate", value: fmtPct(f.showRate), notes: "", rawValue: f.showRate },
          { metric: "Avg Duration", value: `${Math.round(f.avgDuration)}m`, notes: "", rawValue: f.avgDuration },
          ...regTimingPoints.map((p) => ({
            metric: p.label,
            value: `${p.showRate.toFixed(1)}%`,
            notes: `${p.attended} showed / ${p.registered} registered`,
            rawValue: p.showRate,
          })),
        ];
        zoomRows.forEach((r) => {
          const tgt = resolveTarget(r.metric, r.rawValue, ZOOM_TARGETS);
          sheetRows.push({ section: "Zoom Attendance", metric: r.metric, value: r.value, notes: r.notes, target: tgt?.rangeStr, indicator: tgt?.indicator });
        });
      }

      // Build sales rows for one block (used in multi-view export)
      const buildBlockRows = (block: SalesMetricsBlock | null, sectionLabel: string, isTotalView: boolean): SheetRow[] => {
        if (!block) return [];
        const rows: SheetRow[] = [];
        const push = (metric: string, value: string, notes: string, rawValue?: number) => {
          const tgt = resolveTarget(metric, rawValue, SALES_TARGETS);
          rows.push({ section: sectionLabel, metric, value, notes, target: tgt?.rangeStr, indicator: tgt?.indicator });
        };
        push("Opt-ins", fmtN(block.optins), "", block.optins);
        if (f.landingPageVisitors > 0) push("Landing Page Visitors", fmtN(f.landingPageVisitors), "");
        if (f.optinRate > 0) push("Opt-in Rate", fmtPct(f.optinRate), "", f.optinRate);
        push("Total Apps", fmtN(block.apps), "", block.apps);
        push("Full Apps", fmtN(block.fullApps), "", block.fullApps);
        push("Qualified Apps", fmtN(block.qualifiedApps), "", block.qualifiedApps);
        push("Calls Booked", fmtN(block.bookedCalls), "", block.bookedCalls);
        const taken = isTotalView && enh ? enh.totalEocsTaken : block.takenCalls;
        push("Calls Taken", fmtN(taken), "");
        push("Closes", fmtN(block.closed), "");
        if (block.deposits > 0) push("Deposits", fmtN(block.deposits), "");
        push("Close Rate", fmtPct(block.closeRate), "", block.closeRate);
        push("Cash", fmt$(block.cash), "");
        push("Cash from Opt-ins", fmt$(block.cashFromOptins), "");
        push("Cash from Applications", fmt$(block.cashFromApplications), "");
        push("Cash from Booked Calls", fmt$(block.cashFromBookedCalls), "");
        if (block.auv > 0) push("AOV", fmt$(block.auv), "");
        if (block.showRate > 0) push("Show Rate", fmtPct(block.showRate), "", block.showRate);
        const fab = block.appToBookingRate;
        push("Full App→Booking", fmtPct(fab), `${fmtN(block.fullApps)} / ${fmtN(block.bookedCalls)}`, fab);
        const ob = block.optins > 0 ? (block.bookedCalls / block.optins) * 100 : 0;
        push("Optin→Booking", fmtPct(ob), `${fmtN(block.optins)} / ${fmtN(block.bookedCalls)}`, ob);
        const ofa = block.optins > 0 ? (block.fullApps / block.optins) * 100 : 0;
        push("Opt-in→Full App", fmtPct(ofa), `${fmtN(block.optins)} / ${fmtN(block.fullApps)}`, ofa);
        const oqa = block.optins > 0 ? (block.qualifiedApps / block.optins) * 100 : 0;
        push("Optin→Qual App", fmtPct(oqa), `${fmtN(block.optins)} / ${fmtN(block.qualifiedApps)}`, oqa);
        push("Qual→Booking", fmtPct(block.qualifiedToBookingRate), `${fmtN(block.qualifiedApps)} / ${fmtN(block.bookedCalls)}`, block.qualifiedToBookingRate);
        return rows;
      };

      // Export rows = all three sales views + on-webinar + zoom (always complete)
      const exportRows: SheetRow[] = [
        ...sheetRows.filter((r) => r.section === "Webinar Info"),
        ...buildBlockRows(totalBlock, "Blended (All) — Sales", true),
        ...buildBlockRows(onBlock, "On Webinar — Sales", false),
        ...buildBlockRows(postBlock, "Post Webinar — Sales", false),
        ...sheetRows.filter((r) => r.section === "On-Webinar Metrics"),
        ...sheetRows.filter((r) => r.section === "Zoom Attendance"),
      ];

      // Download helpers
      const webinarLabel = `${f.webinarName ?? id} — ${formatDate(f.webinarDate)}`;

      const downloadCSV = () => {
        const lines = [`"Webinar","${webinarLabel.replace(/"/g, '""')}"`, `"Section","Metric","Value","Target","Notes"`];
        exportRows.forEach((r) => {
          lines.push([r.section, r.metric, r.value, r.target ?? "", r.notes].map((v) => `"${v.replace(/"/g, '""')}"`).join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
        a.download = `${id}-metrics.csv`; a.click();
      };

      const downloadExcel = () => {
        const wb = XLSX.utils.book_new();
        const colWidths = [{ wch: 30 }, { wch: 16 }, { wch: 14 }, { wch: 30 }];

        const addSheet = (rows: SheetRow[], sheetName: string, header: string) => {
          const data: string[][] = [
            [header, webinarLabel], [],
            ["Metric", "Value", "Target", "Notes"],
            ...rows.map((r) => [r.metric, r.value, r.target ?? "", r.notes]),
          ];
          const ws = XLSX.utils.aoa_to_sheet(data);
          ws["!cols"] = colWidths;
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        };

        // Webinar info sheet
        const infoRows = sheetRows.filter((r) => r.section === "Webinar Info");
        if (infoRows.length > 0) addSheet(infoRows, "Info", "Webinar Info");

        // Three sales views as separate tabs
        const blendedRows = buildBlockRows(totalBlock, "Blended (All)", true);
        if (blendedRows.length > 0) addSheet(blendedRows, "Blended", "Blended — All Sales");

        const onWebinarSalesRows = buildBlockRows(onBlock, "On Webinar", false);
        if (onWebinarSalesRows.length > 0) addSheet(onWebinarSalesRows, "On Webinar", "On Webinar Sales");

        const postWebinarSalesRows = buildBlockRows(postBlock, "Post Webinar", false);
        if (postWebinarSalesRows.length > 0) addSheet(postWebinarSalesRows, "Post Webinar", "Post Webinar Sales");

        // On-webinar metrics
        const owRows = sheetRows.filter((r) => r.section === "On-Webinar Metrics");
        if (owRows.length > 0) addSheet(owRows, "On-Webinar Metrics", "On-Webinar Metrics");

        // Zoom attendance
        const zoomRows = sheetRows.filter((r) => r.section === "Zoom Attendance");
        if (zoomRows.length > 0) addSheet(zoomRows, "Zoom Attendance", "Zoom Attendance");

        XLSX.writeFile(wb, `${id}-metrics.xlsx`);
      };

      const downloadPDF = () => window.print();

      // Group rows by section for rendering
      const sections: { title: string; rows: SheetRow[] }[] = [];
      sheetRows.forEach((r) => {
        const last = sections[sections.length - 1];
        if (last && last.title === r.section) { last.rows.push(r); }
        else { sections.push({ title: r.section, rows: [r] }); }
      });

      let globalRowNum = 1;

      return (
        <div className="space-y-3">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-foreground">{f.webinarName ?? formatDate(def?.date ?? "")}</h2>
              <p className="text-[10px] text-muted-foreground">{formatDate(f.webinarDate)}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {enh && (
                <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                  {(["total", "on-webby", "post-webby"] as const).map((v) => (
                    <button key={v} onClick={() => setMetricsView(v)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${metricsView === v ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                      {v === "total" ? "All" : v === "on-webby" ? "On Webinar" : "Post Webinar"}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={downloadCSV}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/50 transition-colors">
                ↓ CSV
              </button>
              <button onClick={downloadExcel}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/50 transition-colors">
                ↓ Excel
              </button>
              <button onClick={downloadPDF}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/50 transition-colors">
                ↓ PDF
              </button>
            </div>
          </div>

          {/* Spreadsheet table — always white/black regardless of theme */}
          <div className="overflow-auto rounded-lg shadow-sm" style={{ border: "1px solid #c6c6c6" }}>
            <table className="w-full text-[12px] border-collapse" style={{ fontFamily: "system-ui, sans-serif", backgroundColor: "#ffffff" }}>
              <thead>
                <tr style={{ background: `linear-gradient(90deg, ${branding.colors.gradient.start} 0%, ${branding.colors.gradient.end} 100%)` }}>
                  <th style={{ width: 32, padding: "6px 8px", textAlign: "right", fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,0.5)", borderRight: "1px solid rgba(255,255,255,0.15)" }} className="select-none"></th>
                  <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.06em", borderRight: "1px solid rgba(255,255,255,0.15)" }}>Metric</th>
                  <th style={{ width: 140, padding: "6px 12px", textAlign: "right", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.06em", borderRight: "1px solid rgba(255,255,255,0.15)" }}>Value</th>
                  <th style={{ width: 160, padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.06em", borderRight: "1px solid rgba(255,255,255,0.15)" }}>Target</th>
                  <th style={{ width: 220, padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", letterSpacing: "0.06em" }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((sec) => (
                  <>
                    {/* Section header row */}
                    <tr key={`sec-${sec.title}`} style={{ background: `linear-gradient(90deg, ${branding.colors.gradient.start} 0%, ${branding.colors.gradient.end} 100%)` }}>
                      <td style={{ padding: "4px 8px", borderRight: "1px solid rgba(255,255,255,0.15)", borderBottom: "1px solid rgba(255,255,255,0.1)" }} className="select-none"></td>
                      <td colSpan={4} style={{ padding: "4px 12px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.1em" }}>{sec.title}</span>
                      </td>
                    </tr>
                    {/* Data rows */}
                    {sec.rows.map((row, ri) => {
                      const rn = globalRowNum++;
                      const isEven = ri % 2 === 0;
                      const indColor = row.indicator === "above" ? "#22c55e" : row.indicator === "below" ? "#ef4444" : "#eab308";
                      return (
                        <tr key={`${sec.title}-${ri}`}
                          style={{ backgroundColor: isEven ? "#ffffff" : "#f8f9fa" }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e8f0fe")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isEven ? "#ffffff" : "#f8f9fa")}>
                          <td style={{ padding: "3px 8px", textAlign: "right", fontSize: 10, color: "#80868b", backgroundColor: "#f1f3f4", border: "1px solid #d3d3d3", width: 32, userSelect: "none" }}>{rn}</td>
                          <td style={{ padding: "3px 12px", border: "1px solid #d3d3d3", color: "#202124" }}>{row.metric}</td>
                          <td style={{ padding: "3px 12px", textAlign: "right", border: "1px solid #d3d3d3", color: "#202124", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{row.value || "—"}</td>
                          <td style={{ padding: "4px 12px", border: "1px solid #d3d3d3" }}>
                            {row.target && row.indicator ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {makeTrendIcon(row.indicator, indColor)}
                                <span style={{ fontSize: 11, fontWeight: 600, color: indColor }}>{row.target}</span>
                              </div>
                            ) : null}
                          </td>
                          <td style={{ padding: "3px 12px", border: "1px solid #d3d3d3", color: "#5f6368", fontSize: 11 }}>{row.notes}</td>
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {/* Header with name, date, badges, ad spend — outside card for clean screenshots */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className={`${isCompare ? "text-sm" : "text-base"} font-bold text-foreground`}>{f.webinarName ?? formatDate(def?.date ?? "")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{formatDate(f.webinarDate)}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {!isCompare && (
              <span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${
                aud === "warm"
                  ? "bg-orange-500/10 text-orange-400 border-orange-500/20"
                  : "bg-blue-500/10 text-blue-400 border-blue-500/20"
              }`}>{aud === "warm" ? "Warm Traffic" : "Cold Traffic"}</span>
            )}
            {!f.hasZoomData && (
              <span className="text-[10px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded px-2 py-0.5">No Zoom data</span>
            )}
            {!isCompare && (
              <>
                <button onClick={() => setUploadModal(id)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-card text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors">
                  ↑ Zoom CSV
                </button>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground">Ad Spend $</span>
                  <input type="number" min="0" value={adSpendInput[id] ?? ""} onChange={(e) => setAdSpendInput((prev) => ({ ...prev, [id]: e.target.value }))} placeholder="0"
                    className="w-20 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                  <button onClick={() => saveAdSpend(id)} disabled={savingAdSpend === id}
                    className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                    {savingAdSpend === id ? "…" : "Save"}
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground">📅</span>
                  <input type="number" min="0" value={calendarInput[id] ?? ""} onChange={(e) => setCalendarInput((prev) => ({ ...prev, [id]: e.target.value }))} placeholder="Cal adds"
                    className="w-20 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                  <span className="text-[10px] text-muted-foreground">💬</span>
                  <input type="number" min="0" value={whatsappInput[id] ?? ""} onChange={(e) => setWhatsappInput((prev) => ({ ...prev, [id]: e.target.value }))} placeholder="WhatsApp"
                    className="w-20 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                  <button onClick={() => saveEngagementMetrics(id)} disabled={savingEngagement === id}
                    className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                    {savingEngagement === id ? "…" : "Save"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sales metrics card — clean for screenshots */}
        <div className="bg-card border border-border rounded-xl p-4">
          {/* Metrics perspective toggle — Total / On Webby / Post Webby */}
          {enh && (
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sales Metrics</h3>
                {hiddenMetrics.size > 0 && (
                  <button onClick={() => setHiddenMetrics(new Set())}
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/20 transition-colors">
                    ↺ Reset ({hiddenMetrics.size})
                  </button>
                )}
              </div>
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["total", "on-webby", "post-webby"] as const).map((v) => (
                  <button key={v} onClick={() => setMetricsView(v)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      metricsView === v ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {v === "total" ? "Total" : v === "on-webby" ? "On Webby" : "Post Webby"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sales scorecards with inline proportion bars (drag to reorder) */}
          {(() => {
            const visibleKpis = salesKpisWithDeltas.filter((s) => !hiddenMetrics.has(s.l));
            const savedOrder = salesCardOrder.length > 0
              ? [
                  ...salesCardOrder.filter((l) => visibleKpis.some((k) => k.l === l)).map((l) => visibleKpis.find((k) => k.l === l)!),
                  ...visibleKpis.filter((k) => !salesCardOrder.includes(k.l)),
                ].filter((k): k is KpiCard => k !== undefined)
              : visibleKpis;
            // Keep these four funnel stages adjacent even when a browser has a saved drag order.
            const requiredOrder = ["Partial Apps", "Webinar Bookings", "End of Call Reports Filled", "Calls Taken"];
            const requiredCards = requiredOrder.map((label) => savedOrder.find((card) => card.l === label)).filter((card): card is KpiCard => card !== undefined);
            const remainingCards = savedOrder.filter((card) => !requiredOrder.includes(card.l));
            const partialIndex = remainingCards.findIndex((card) => card.l === "Qualified Apps") + 1;
            const orderedKpis = [
              ...remainingCards.slice(0, Math.max(partialIndex, 0)),
              ...requiredCards,
              ...remainingCards.slice(Math.max(partialIndex, 0)),
            ];
            return (
              <div className={`grid gap-4 ${isCompare ? "grid-cols-2 xl:grid-cols-4" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"}`}>
                {orderedKpis.map((s, i) => {
                  const showBar = !isCompare && metricsView === "total" && !s.isRate && s.totalVal !== undefined && s.onVal !== undefined && s.postVal !== undefined && s.totalVal > 0;
                  const onPct = showBar ? (s.onVal! / s.totalVal!) * 100 : 0;
                  const canClick = s.clickable && STAGE_KEY_MAP[s.l];
                  const isCancelCard = "isCancelCard" in s && s.isCancelCard;
                  const isInteractive = canClick || isCancelCard;
                  const isDragging = dragSrcIdx === i;
                  const isDragOver = dragOverIdx === i && dragSrcIdx !== null && dragSrcIdx !== i;
                  const highlightKey = `${id}-${s.l}`;
                  const highlight = showHighlights ? cardHighlights[highlightKey] : undefined;
                  return (
                    <motion.div
                      key={s.l}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                      draggable
                      onDragStart={(e) => { setDragSrcIdx(i); (e as unknown as DragEvent).dataTransfer!.effectAllowed = "move"; }}
                      onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragSrcIdx !== null && dragSrcIdx !== i) {
                          const newOrder = orderedKpis.map((k) => k.l);
                          const [moved] = newOrder.splice(dragSrcIdx, 1);
                          newOrder.splice(i, 0, moved);
                          setSalesCardOrder(newOrder);
                          localStorage.setItem("cr8her_sales_card_order", JSON.stringify(newOrder));
                        }
                        setDragSrcIdx(null); setDragOverIdx(null);
                      }}
                      onDragEnd={() => { setDragSrcIdx(null); setDragOverIdx(null); }}
                      onClick={isCancelCard
                        ? () => setCancelledModal({ webinarId: id, traffic: traffic as "all" | "paid" | "organic" })
                        : canClick ? () => handleStageClick(id, s.l, traffic as "all" | "paid" | "organic") : undefined}
                      className={`relative group border rounded-lg px-3 py-3 cursor-grab active:cursor-grabbing transition-all flex flex-col ${isCompare ? "h-[148px] overflow-hidden" : ""}
                        ${highlight === "green" ? "bg-green-500/10 border-green-500/40" : highlight === "red" ? "bg-red-500/10 border-red-500/40" : "bg-muted/20 border-border/40"}
                        ${isDragging ? "opacity-40" : ""}
                        ${isDragOver ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : ""}
                        ${!isDragging && !isDragOver && !highlight && isInteractive ? "hover:border-[var(--brand-primary)]/40 hover:bg-muted/30" : ""}
                      `}>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleMetric(s.l); }}
                        className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/40 opacity-0 group-hover:opacity-100 transition-all text-[10px] leading-none"
                        title="Hide metric">×</button>
                      <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all" onClick={(e) => e.stopPropagation()}>
                        {showHighlights && highlight && (
                          <button
                            onClick={() => clearCardHighlight(id, s.l)}
                            className="w-3.5 h-3.5 rounded-full border border-border/60 bg-muted/60 hover:bg-muted flex items-center justify-center text-muted-foreground text-[8px] leading-none transition-all"
                            title="Reset to normal">✕</button>
                        )}
                        {showHighlights && (
                          <button
                            onClick={() => toggleCardHighlight(id, s.l, "green")}
                            className={`w-3.5 h-3.5 rounded-full border transition-all ${highlight === "green" ? "bg-green-500 border-green-500 shadow-[0_0_4px_rgba(34,197,94,0.5)]" : "bg-green-500/20 border-green-500/40 hover:bg-green-500/60"}`}
                            title="Highlight green (up)" />
                        )}
                        {showHighlights && (
                          <button
                            onClick={() => toggleCardHighlight(id, s.l, "red")}
                            className={`w-3.5 h-3.5 rounded-full border transition-all ${highlight === "red" ? "bg-red-500 border-red-500 shadow-[0_0_4px_rgba(239,68,68,0.5)]" : "bg-red-500/20 border-red-500/40 hover:bg-red-500/60"}`}
                            title="Highlight red (down)" />
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{s.l}{isInteractive ? " ↗" : ""}</div>
                      <div className={`${isCompare ? "text-base" : "text-xl"} font-bold ${webinarMetricValueClass} tabular-nums`}>{s.v}</div>
                      <div className="min-h-[20px]">{renderMetricDelta(s.rawValue, "prevRawValue" in s ? s.prevRawValue : undefined)}</div>
                      {s.subCount && <div className="text-[10px] text-muted-foreground/60 tabular-nums mt-0.5">{s.subCount}</div>}
                      {!isCompare && (s.l === "Add to Calendar" || s.l === "Joined WhatsApp") && (
                        <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="number" min="0" placeholder="count"
                            value={s.l === "Add to Calendar" ? (calendarInput[id] ?? "") : (whatsappInput[id] ?? "")}
                            onChange={(e) => s.l === "Add to Calendar"
                              ? setCalendarInput((prev) => ({ ...prev, [id]: e.target.value }))
                              : setWhatsappInput((prev) => ({ ...prev, [id]: e.target.value }))}
                            className="w-16 px-1 py-0.5 text-xs bg-background border border-border rounded focus:outline-none focus:border-[var(--brand-primary)]"
                          />
                          <button
                            onClick={() => saveEngagementMetrics(id)}
                            disabled={savingEngagement === id}
                            className="px-1.5 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                            {savingEngagement === id ? "…" : "Set"}
                          </button>
                        </div>
                      )}
                      {showBar && (
                        <div className="mt-2">
                          <div className="flex items-center gap-1.5 h-1.5 w-full rounded-full overflow-hidden bg-muted/30">
                            <div className="h-full rounded-full bg-[var(--brand-gradient-start)]" style={{ width: `${onPct}%` }} />
                            <div className="h-full rounded-full bg-[var(--brand-accent)] opacity-60" style={{ width: `${100 - onPct}%`, marginLeft: "-6px" }} />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[9px] text-muted-foreground">{s.isCash ? fmt$(s.onVal!) : fmtN(s.onVal!)} on</span>
                            <span className="text-[9px] text-muted-foreground">{s.isCash ? fmt$(s.postVal!) : fmtN(s.postVal!)} post</span>
                          </div>
                        </div>
                      )}
                      <div className="flex-1" />
                      {renderTargetBadge(s.l, s.rawValue, SALES_TARGETS)}
                    </motion.div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Funnel chart */}
        <div className="bg-card border border-border rounded-xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-foreground">Funnel</h3>
            {!isCompare && (
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["all", "compare"] as const).map((mode) => (
                  <button key={mode} onClick={() => setFunnelMode(mode)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      funnelMode === mode ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>{mode}</button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-4">Click any block to view leads</p>
          {!isCompare && funnelMode === "compare" ? (
            <div className="flex gap-3 md:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-center text-muted-foreground mb-3 uppercase tracking-wider">Organic</p>
                <FunnelChart stages={organicStages} gradientId="fgOrganic" mutedValues={isCrimson} onStageClick={(label) => handleStageClick(id, label, "organic")} />
              </div>
              <div className="w-px bg-border/30 self-stretch" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-center text-muted-foreground mb-3 uppercase tracking-wider">Paid</p>
                <FunnelChart stages={paidStages} gradientId="fgPaid" mutedValues={isCrimson} onStageClick={(label) => handleStageClick(id, label, "paid")} />
              </div>
            </div>
          ) : (
            <FunnelChart stages={stages} gradientId={`fg${id}`} mutedValues={isCrimson} onStageClick={(label) => handleStageClick(id, label, isCompare ? traffic : "all")} />
          )}
        </div>

        {/* On-Webinar Metrics */}
        {enh && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm font-semibold text-foreground">On-Webinar Metrics</h3>
              {hiddenOnWebinarMetrics.size > 0 && (
                <button onClick={() => setHiddenOnWebinarMetrics(new Set())}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/20 transition-colors">
                  ↺ Reset ({hiddenOnWebinarMetrics.size})
                </button>
              )}
              {onWebinarCardOrder.length > 0 && (
                <button onClick={() => { setOnWebinarCardOrder([]); localStorage.removeItem("cr8her_on_webinar_card_order"); }}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/20 transition-colors">
                  ↺ Order
                </button>
              )}
            </div>
            {(() => {
              const allOnWebinarMetrics = [
                { l: "Opt-ins", v: fmtN(enh.onWebinar.registrants) },
                { l: "CPR", v: enh.onWebinar.cpr > 0 ? fmt$(enh.onWebinar.cpr) : "—" },
                { l: "Attended", v: fmtN(enh.onWebinar.attended) },
                { l: "Show Rate", v: fmtPct(enh.onWebinar.showRate), subCount: `${fmtN(enh.onWebinar.attended)} / ${fmtN(enh.onWebinar.registrants)}` },
                { l: "Avg Duration", v: `${Math.round(enh.onWebinar.avgDuration)}m` },
                ...(enh.onWebinar.pitchMinute ? [
                  { l: "Pitch Start", v: `${enh.onWebinar.pitchMinute}m` },
                  { l: "@ Pitch", v: fmtPct(enh.onWebinar.retentionAtPitch), subCount: fmtN(enh.onWebinar.attendeesAtPitch) },
                  { l: "Booking Rate", v: fmtPct(enh.onWebinar.attendeesAtPitch > 0 ? (enh.salesTotal.bookedCalls / enh.onWebinar.attendeesAtPitch) * 100 : 0), subCount: `${fmtN(enh.onWebinar.attendeesAtPitch)} / ${fmtN(enh.salesTotal.bookedCalls)}` },
                ] : [
                  { l: "Booking Rate", v: fmtPct(enh.onWebinar.bookingRate) },
                ]),
                // App rates reconcile to the total attributed funnel-app counts.
                ...(enh.onWebinar.attendeesAtPitch > 0 ? [
                  { l: "Total App Rate", v: fmtPct(enh.salesTotal.apps / enh.onWebinar.attendeesAtPitch * 100), subCount: `${fmtN(enh.salesTotal.apps)} / ${fmtN(enh.onWebinar.attendeesAtPitch)}` },
                  { l: "Full App Rate",  v: fmtPct(enh.salesTotal.fullApps / enh.onWebinar.attendeesAtPitch * 100), subCount: `${fmtN(enh.salesTotal.fullApps)} / ${fmtN(enh.onWebinar.attendeesAtPitch)}` },
                  { l: "Qual App Rate",  v: fmtPct(enh.salesTotal.qualifiedApps / enh.onWebinar.attendeesAtPitch * 100), subCount: `${fmtN(enh.onWebinar.attendeesAtPitch)} / ${fmtN(enh.salesTotal.qualifiedApps)}` },
                ] : []),
                ...(enh.dials.totalDials > 0 ? [
                  { l: "Total Dials", v: fmtN(enh.dials.totalDials) },
                  { l: "Pickup Rate", v: fmtPct(enh.dials.pickupRate) },
                ] : []),
              ];
              const onWebinarRawValue = (label: string, sourceEnh: EnhancedWebinarData | null) => {
                if (!sourceEnh) return undefined;
                if (label === "Opt-ins") return sourceEnh.onWebinar.registrants;
                if (label === "CPR") return sourceEnh.onWebinar.cpr;
                if (label === "Attended") return sourceEnh.onWebinar.attended;
                if (label === "Show Rate") return sourceEnh.onWebinar.showRate;
                if (label === "Avg Duration") return sourceEnh.onWebinar.avgDuration;
                if (label === "Pitch Start") return sourceEnh.onWebinar.pitchMinute ?? undefined;
                if (label === "@ Pitch") return sourceEnh.onWebinar.retentionAtPitch;
                if (label === "Booking Rate") return sourceEnh.onWebinar.attendeesAtPitch > 0
                  ? (sourceEnh.salesTotal.bookedCalls / sourceEnh.onWebinar.attendeesAtPitch) * 100
                  : sourceEnh.onWebinar.bookingRate;
                if (label === "Total App Rate") return sourceEnh.onWebinar.attendeesAtPitch > 0 ? (sourceEnh.salesTotal.apps / sourceEnh.onWebinar.attendeesAtPitch) * 100 : undefined;
                if (label === "Full App Rate") return sourceEnh.onWebinar.attendeesAtPitch > 0 ? (sourceEnh.salesTotal.fullApps / sourceEnh.onWebinar.attendeesAtPitch) * 100 : undefined;
                if (label === "Qual App Rate") return sourceEnh.onWebinar.attendeesAtPitch > 0 ? (sourceEnh.salesTotal.qualifiedApps / sourceEnh.onWebinar.attendeesAtPitch) * 100 : undefined;
                if (label === "Total Dials") return sourceEnh.dials.totalDials;
                if (label === "Pickup Rate") return sourceEnh.dials.pickupRate;
                return undefined;
              };
              const visibleOnWebinar = allOnWebinarMetrics
                .map((k) => ({ ...k, rawValue: onWebinarRawValue(k.l, enh), prevRawValue: onWebinarRawValue(k.l, prevEnh) }))
                .filter((s) => !hiddenOnWebinarMetrics.has(s.l));
              const orderedOnWebinar = onWebinarCardOrder.length > 0
                ? [
                    ...onWebinarCardOrder.filter((l) => visibleOnWebinar.some((k) => k.l === l)).map((l) => visibleOnWebinar.find((k) => k.l === l)!),
                    ...visibleOnWebinar.filter((k) => !onWebinarCardOrder.includes(k.l)),
                  ].filter((k): k is { l: string; v: string; rawValue?: number; prevRawValue?: number; subCount?: string } => k !== undefined)
                : visibleOnWebinar;
              return (
                <div className={`grid ${isCompare ? "grid-cols-2 xl:grid-cols-3" : "grid-cols-2 md:grid-cols-4"} gap-4 mb-4`}>
                  {orderedOnWebinar.map((s, i) => {
                    const isOwDragging = dragOnWebinarSrcIdx === i;
                    const isOwDragOver = dragOnWebinarOverIdx === i && dragOnWebinarSrcIdx !== null && dragOnWebinarSrcIdx !== i;
                    return (
                      <motion.div key={s.l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        draggable
                        onDragStart={(e) => { setDragOnWebinarSrcIdx(i); (e as unknown as DragEvent).dataTransfer!.effectAllowed = "move"; }}
                        onDragOver={(e) => { e.preventDefault(); setDragOnWebinarOverIdx(i); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragOnWebinarSrcIdx !== null && dragOnWebinarSrcIdx !== i) {
                            const newOrder = orderedOnWebinar.map((k) => k.l);
                            const [moved] = newOrder.splice(dragOnWebinarSrcIdx, 1);
                            newOrder.splice(i, 0, moved);
                            setOnWebinarCardOrder(newOrder);
                            localStorage.setItem("cr8her_on_webinar_card_order", JSON.stringify(newOrder));
                          }
                          setDragOnWebinarSrcIdx(null); setDragOnWebinarOverIdx(null);
                        }}
                        onDragEnd={() => { setDragOnWebinarSrcIdx(null); setDragOnWebinarOverIdx(null); }}
                        className={`relative group border rounded-lg px-3 py-3 cursor-grab active:cursor-grabbing transition-all flex flex-col ${isCompare ? "h-[148px] overflow-hidden" : ""}
                          ${showHighlights && cardHighlights[`${id}-ow-${s.l}`] === "green" ? "bg-green-500/10 border-green-500/40" : showHighlights && cardHighlights[`${id}-ow-${s.l}`] === "red" ? "bg-red-500/10 border-red-500/40" : "bg-muted/20 border-border/40"}
                          ${isOwDragging ? "opacity-40" : ""}
                          ${isOwDragOver ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : ""}
                        `}>
                        <button
                          onClick={() => toggleOnWebinarMetric(s.l)}
                          className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/40 opacity-0 group-hover:opacity-100 transition-all text-[10px] leading-none"
                          title="Hide metric">×</button>
                        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all" onClick={(e) => e.stopPropagation()}>
                          {showHighlights && cardHighlights[`${id}-ow-${s.l}`] && (
                            <button onClick={() => clearCardHighlight(id, `ow-${s.l}`)}
                              className="w-3.5 h-3.5 rounded-full border border-border/60 bg-muted/60 hover:bg-muted flex items-center justify-center text-muted-foreground text-[8px] leading-none transition-all"
                              title="Reset to normal">✕</button>
                          )}
                          {showHighlights && (
                            <button onClick={() => toggleCardHighlight(id, `ow-${s.l}`, "green")}
                              className={`w-3.5 h-3.5 rounded-full border transition-all ${cardHighlights[`${id}-ow-${s.l}`] === "green" ? "bg-green-500 border-green-500 shadow-[0_0_4px_rgba(34,197,94,0.5)]" : "bg-green-500/20 border-green-500/40 hover:bg-green-500/60"}`}
                              title="Highlight green (up)" />
                          )}
                          {showHighlights && (
                            <button onClick={() => toggleCardHighlight(id, `ow-${s.l}`, "red")}
                              className={`w-3.5 h-3.5 rounded-full border transition-all ${cardHighlights[`${id}-ow-${s.l}`] === "red" ? "bg-red-500 border-red-500 shadow-[0_0_4px_rgba(239,68,68,0.5)]" : "bg-red-500/20 border-red-500/40 hover:bg-red-500/60"}`}
                              title="Highlight red (down)" />
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{s.l}</div>
                        <div className={`${isCompare ? "text-base" : "text-xl"} font-bold ${webinarMetricValueClass} tabular-nums`}>{s.v}</div>
                        <div className="min-h-[20px]">{renderMetricDelta(s.rawValue, "prevRawValue" in s ? s.prevRawValue : undefined)}</div>
                        {"subCount" in s && s.subCount && <div className="text-[10px] text-muted-foreground/60 tabular-nums mt-0.5">{s.subCount}</div>}
                        <div className="flex-1" />
                        {renderTargetBadge(s.l, s.rawValue, ON_WEBINAR_TARGETS)}
                      </motion.div>
                    );
                  })}
                </div>
              );
            })()}
            {!isCompare && (
              <div className="flex items-center gap-2 pt-3 border-t border-border/30">
                <span className="text-[10px] text-muted-foreground">Pitch starts at minute</span>
                <input type="number" min="0" placeholder="e.g. 45" value={pitchMinuteInput[id] ?? ""}
                  onChange={(e) => setPitchMinuteInput((prev) => ({ ...prev, [id]: e.target.value }))}
                  className="w-16 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                <button onClick={() => savePitchMinute(id)} disabled={savingPitchMinute === id}
                  className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                  {savingPitchMinute === id ? "…" : "Set"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Zoom Attendance + Retention */}
        {f.hasZoomData && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Zoom Attendance</h3>
            <div className={`grid ${isCompare ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"} gap-4 mb-5`}>
              {[
                { l: "Registrants",  v: fmtN(f.totalRegistrants), rawValue: f.totalRegistrants, prevRawValue: prevF?.totalRegistrants },
                { l: "Attended",     v: fmtN(f.totalAttended), rawValue: f.totalAttended, prevRawValue: prevF?.totalAttended },
                { l: "Show Rate",    v: fmtPct(f.showRate), rawValue: f.showRate, prevRawValue: prevF?.showRate },
                { l: "Avg Duration", v: `${Math.round(f.avgDuration)}m`, rawValue: f.avgDuration, prevRawValue: prevF?.avgDuration },
              ].map((s, i) => (
                <motion.div key={s.l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                  <div className={`${isCompare ? "text-sm" : "text-lg"} font-bold text-foreground`}>{s.v}</div>
                  {renderMetricDelta(s.rawValue, s.prevRawValue)}
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">{s.l}</div>
                </motion.div>
              ))}
            </div>
            {ret.length > 0 && (
              <>
                <p className="text-xs text-muted-foreground mb-3">Audience retention — % still watching by minute</p>
                <ResponsiveContainer width="100%" height={isCompare ? 140 : 180}>
                  <AreaChart data={ret} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`retGrad${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={branding.colors.gradient.start} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={branding.colors.gradient.start} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${v}m`} />
                    <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      labelFormatter={(v) => `${v} min`} formatter={(_v: number, _name: string, item: { payload: RetentionPoint }) => [String(item.payload.count), "Still watching"]} />
                    {pitchMin != null && (
                      <ReferenceLine x={pitchMin} stroke="rgba(255,255,255,0.35)" strokeDasharray="4 3"
                        label={{ value: "▼ Pitch", position: "insideTopLeft", fontSize: 9, fill: "rgba(255,255,255,0.5)", dy: -2 }} />
                    )}
                    <Area type="monotone" dataKey="pct" stroke={branding.colors.gradient.start} strokeWidth={2} fill={`url(#retGrad${id})`} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
            {/* Retention by source */}
            {retSources.length > 1 && (
              <div className="mt-5 pt-4 border-t border-border/30">
                <RetentionBySourceChart data={retBySrc} sources={retSources} colors={branding.colors.chart} pitchMinute={pitchMin} sourceTotals={srcTotals} />
              </div>
            )}
            {/* Cash by source + opt-in attribution + creator attendee show rates */}
            {(isCompare || cashBySrc.length > 0 || creatorDistributions || optinsByUtm || attendeesByCreator.length > 0) && (
              <div className={`mt-5 pt-4 border-t border-border/30 grid gap-8 ${isCompare ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
                {(isCompare || cashBySrc.length > 0) && <div><div className="flex items-center justify-end gap-1 mb-2"><button onClick={() => setCashGroupBy("platform")} className={`px-2 py-1 text-[10px] rounded ${cashGroupBy === "platform" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground"}`}>Source Platform</button><button onClick={() => setCashGroupBy("creator")} className={`px-2 py-1 text-[10px] rounded ${cashGroupBy === "creator" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground"}`}>Source Creator</button></div><CashBySourceChart data={cashBySrc} title="Cash Collected by Traffic Source/Creator" /></div>}
                {creatorDistributions && <div><div className="flex items-center justify-end gap-1 mb-2"><button onClick={() => setApplicationPieType("all")} className={`px-2 py-1 text-[10px] rounded ${applicationPieType === "all" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground"}`}>Total</button><button onClick={() => setApplicationPieType("full")} className={`px-2 py-1 text-[10px] rounded ${applicationPieType === "full" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground"}`}>Full</button><button onClick={() => setApplicationPieType("partial")} className={`px-2 py-1 text-[10px] rounded ${applicationPieType === "partial" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground"}`}>Partial</button></div><CreatorDistributionPieChart data={applicationCreatorData} title={`Applications by Creator — ${metricsView === "on-webby" ? "On Webby" : metricsView === "post-webby" ? "Post Webby" : "Total"}`} /></div>}
                {creatorDistributions && <CreatorDistributionPieChart data={bookingCreatorData} title={`Booked Calls by Creator — ${metricsView === "on-webby" ? "On Webby" : metricsView === "post-webby" ? "Post Webby" : "Total"}`} />}
                {optinsByUtm && optinsByUtm.byPlatform.length > 0 && (
                  <OptinsByUtmPieChart data={optinsByUtm.byPlatform} title="Opt-ins by Platform (UTM Source)" />
                )}
                {optinsByUtm && optinsByUtm.byCreator.length > 0 && (
                  <OptinsByUtmPieChart data={optinsByUtm.byCreator} title="Opt-ins by Creator (UTM Campaign)" />
                )}
                {attendeesByCreator.length > 0 && <AttendeesByCreatorPieChart data={attendeesByCreator} />}
              </div>
            )}
          </div>
        )}

        {/* Registration Timing vs Show Rate */}
        {regTimingPoints.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-xs font-bold tracking-widest uppercase text-white">Registration Timing vs Show Rate</h3>
              <span className="text-xs text-muted-foreground">Show Rate</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Show rate by when registrants signed up (earliest → day of)</p>
            <ResponsiveContainer width="100%" height={isCompare ? 140 : 200}>
              <AreaChart data={regTimingPoints} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="regTimingFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={branding.colors.secondary} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={branding.colors.secondary} stopOpacity={0} />
                  </linearGradient>
                  <filter id="regTimingGlow" x="-20%" y="-40%" width="140%" height="180%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.35)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.35)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                  formatter={(_v: number, _name: string, item: { payload: RegTimingPoint }) => [
                    `${item.payload.attended} / ${item.payload.registered} (${item.payload.showRate.toFixed(1)}%)`,
                    "Showed",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="showRate"
                  stroke={branding.colors.secondary}
                  strokeWidth={2.5}
                  fill="url(#regTimingFill)"
                  dot={false}
                  activeDot={{ r: 4, fill: branding.colors.secondary, strokeWidth: 0 }}
                  filter="url(#regTimingGlow)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Call Show Rate by Day chart */}
        {enh && enh.callShowRateByDay && enh.callShowRateByDay.some((d) => d.eocsDue > 0) && (
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="mb-1">
              <h3 className="text-sm font-semibold text-foreground">Call Show Rate — Day of Webinar Through Day +5</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                EOC-based show rate (calls taken ÷ calls due) by day relative to the webinar. Shows where show rates dip as days pass.
              </p>
            </div>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={enh.callShowRateByDay.filter((d) => d.eocsDue > 0)} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    formatter={(_v: number, _name: string, item: { payload: { eocsDue: number; eocsTaken: number; showRate: number } }) => [
                      `${item.payload.eocsTaken} / ${item.payload.eocsDue} calls due (${item.payload.showRate.toFixed(1)}%)`,
                      "Show Rate",
                    ]}
                  />
                  <Bar dataKey="showRate" radius={[4, 4, 0, 0]} maxBarSize={56}>
                    {enh.callShowRateByDay.filter((d) => d.eocsDue > 0).map((entry) => (
                      <Cell key={entry.dayOffset} fill={
                        entry.showRate >= 70 ? "var(--brand-primary)" :
                        entry.showRate >= 50 ? "#f59e0b" :
                        "#ef4444"
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Day-by-day breakdown table */}
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {enh.callShowRateByDay.map((d) => (
                <div key={d.dayOffset} className={`rounded-lg border px-2 py-2 text-center ${
                  d.eocsDue === 0 ? "border-border/30 opacity-40" :
                  d.showRate >= 70 ? "border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5" :
                  d.showRate >= 50 ? "border-yellow-500/40 bg-yellow-500/5" :
                  "border-red-500/40 bg-red-500/5"
                }`}>
                  <p className="text-[10px] font-medium text-muted-foreground">{d.label}</p>
                  <p className={`mt-0.5 text-base font-bold tabular-nums ${
                    d.eocsDue === 0 ? "text-muted-foreground" :
                    d.showRate >= 70 ? "text-[var(--brand-primary)]" :
                    d.showRate >= 50 ? "text-yellow-400" :
                    "text-red-400"
                  }`}>{d.eocsDue === 0 ? "—" : `${Math.round(d.showRate)}%`}</p>
                  <p className="text-[9px] text-muted-foreground">{d.eocsDue === 0 ? "no calls" : `${d.eocsTaken}/${d.eocsDue}`}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enhanced loading indicator */}
        {enhLoading && (
          <div className="bg-card border border-border rounded-xl p-8 flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Loading enhanced metrics…</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Webinars</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {selectedIds.length > 1 ? `Comparing ${selectedIds.length} webinars · % change is vs previous selected webinar` : "Funnel performance by event"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSingleView && primaryId && (
            <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
              {(["card", "list"] as const).map((m) => (
                <button key={m} onClick={() => setViewMode(m)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    viewMode === m ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                  }`}>
                  {m === "card" ? "⊞ Cards" : "≡ List"}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5">
            <span className="px-1.5 text-[10px] font-medium text-muted-foreground">All-time blended</span>
            <button onClick={() => downloadBlendedWebinarExport("csv")} disabled={!!exportingBlendedWebinars}
              className="rounded px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50">
              {exportingBlendedWebinars === "csv" ? "Preparing…" : "↓ CSV"}
            </button>
            <button onClick={() => downloadBlendedWebinarExport("xlsx")} disabled={!!exportingBlendedWebinars}
              className="rounded px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50">
              {exportingBlendedWebinars === "xlsx" ? "Preparing…" : "↓ Excel"}
            </button>
            <button onClick={() => downloadBlendedWebinarExport("pdf")} disabled={!!exportingBlendedWebinars}
              className="rounded px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50">
              {exportingBlendedWebinars === "pdf" ? "Preparing…" : "↓ PDF"}
            </button>
          </div>
          <button onClick={() => setAddWebinarModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--brand-primary)] text-white text-xs font-medium hover:opacity-90 transition-opacity">
            + Add Webinar
          </button>
        </div>
      </div>


      {/* Filters row — only shown when there's filter data to choose from */}
      {(uniqueFunnels.length > 0 || uniquePlatforms.length > 0 || uniqueCreators.length > 0) && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Filter:</span>
          {uniqueFunnels.length > 0 && (
            <select
              value={funnelFilter}
              onChange={(e) => setFunnelFilter(e.target.value)}
              className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]"
            >
              <option value="">All Funnels</option>
              {uniqueFunnels.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          )}
          {uniquePlatforms.length > 0 && (
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]"
            >
              <option value="">All Platforms</option>
              {uniquePlatforms.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          )}
          {uniqueCreators.length > 0 && (
            <select
              value={creatorFilter}
              onChange={(e) => setCreatorFilter(e.target.value)}
              className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]"
            >
              <option value="">All Creators</option>
              {uniqueCreators.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          )}
          {(funnelFilter || platformFilter || creatorFilter) && (
            <button
              onClick={() => { setFunnelFilter(""); setPlatformFilter(""); setCreatorFilter(""); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Webinar selector */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {filteredWebinars.map((w) => (
          <button key={w.id} onClick={() => toggleSelect(w.id)}
            className={`flex-shrink-0 px-3 py-2 rounded-xl border text-left transition-all ${
              selectedIds.includes(w.id)
                ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10"
                : "border-border bg-card hover:border-border/70"
            }`}>
            <div className={`text-xs font-bold whitespace-nowrap ${selectedIds.includes(w.id) ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
              {formatDate(w.date)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{w.id}</div>
          </button>
        ))}
      </div>

      {/* Split by traffic filter — under webinar selector */}
      {primaryId && (
        <div className="space-y-2.5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Split by traffic:</span>
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["all", "organic", "paid"] as const).map((t) => (
                  <button key={t} onClick={() => { setTrafficFilter(t); if (!isSingleView) { const updated: Record<string, "all" | "paid" | "organic"> = {}; selectedIds.forEach((sid) => { updated[sid] = t; }); setCardTraffic(updated); } }}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      trafficFilter === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Apps:</span>
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["all", "full", "partial"] as const).map((t) => (
                  <button key={t} onClick={() => setAppTypeFilter(t)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      appTypeFilter === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>{t}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Segment filters — filter opt-in pool by UTM dimension */}
          {filterOptions && (filterOptions.sources.length > 0 || filterOptions.campaigns.length > 0 || filterOptions.contents.length > 0 || filterOptions.mediums.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Segment:</span>
              {filterOptions.sources.length > 0 && (
                <select value={segmentSource} onChange={(e) => setSegmentSource(e.target.value)}
                  className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]">
                  <option value="">All Platforms</option>
                  {buildFilterOptions(filterOptions.sources).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </select>
              )}
              {filterOptions.mediums.length > 0 && (
                <select value={segmentMedium} onChange={(e) => setSegmentMedium(e.target.value)}
                  className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]">
                  <option value="">All Funnels</option>
                  {buildFilterOptions(filterOptions.mediums).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </select>
              )}
              {filterOptions.campaigns.length > 0 && (
                <select value={segmentCampaign} onChange={(e) => setSegmentCampaign(e.target.value)}
                  className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]">
                  <option value="">All Campaigns</option>
                  {buildFilterOptions(filterOptions.campaigns).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </select>
              )}
              {filterOptions.contents.length > 0 && (
                <select value={segmentContent} onChange={(e) => setSegmentContent(e.target.value)}
                  className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:outline-none focus:border-[var(--brand-primary)]">
                  <option value="">All Content</option>
                  {buildFilterOptions(filterOptions.contents).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </select>
              )}
              {(segmentSource || segmentMedium || segmentCampaign || segmentContent) && (
                <button onClick={() => { setSegmentSource(""); setSegmentMedium(""); setSegmentCampaign(""); setSegmentContent(""); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors">Clear</button>
              )}
            </div>
          )}

          {/* Investment level multi-select chips */}
          {filterOptions && filterOptions.investmentLevels.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Investment:</span>
              {filterOptions.investmentLevels.map((lvl) => {
                const isActive = selectedInvestmentLevels.includes(lvl);
                return (
                  <button key={lvl}
                    onClick={() => setSelectedInvestmentLevels((prev) =>
                      prev.includes(lvl) ? prev.filter((x) => x !== lvl) : [...prev, lvl]
                    )}
                    className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${
                      isActive
                        ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white"
                        : "bg-muted/40 border-border text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/40"
                    }`}>
                    {lvl}
                  </button>
                );
              })}
              {selectedInvestmentLevels.length > 0 && (
                <button onClick={() => setSelectedInvestmentLevels([])}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors">Clear</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── SINGLE WEBINAR VIEW ──────────────────────────────────────────────── */}
      {isSingleView && primaryId && renderWebinarContent(primaryId)}
      {isSingleView && !primaryId && (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">Select a webinar</div>
      )}

      {/* ── MULTI-WEBINAR COMPARISON VIEW ────────────────────────────────────── */}
      {!isSingleView && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {selectedIds.map((id, idx) => (
            <div key={id}>
              {renderWebinarContent(id, true, idx === 0)}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {uploadModal && (
          <ZoomUploadModal
            webinarId={uploadModal}
            webinarName={webinars.find((w) => w.id === uploadModal)?.name ?? uploadModal}
            onClose={() => setUploadModal(null)}
            onSuccess={() => handleUploadSuccess(uploadModal)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addWebinarModal && (
          <AddWebinarModal
            onClose={() => setAddWebinarModal(false)}
            onSuccess={handleAddWebinarSuccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {investmentModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setInvestmentModal(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-base font-bold">Investment Level Breakdown</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">{investmentModal.webinarName} · Full Applications</p>
                </div>
                <button onClick={() => setInvestmentModal(null)} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
              </div>

              {Object.keys(investmentModal.breakdown).length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No investment data available — re-sync sheets to populate.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(investmentModal.breakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([level, count]) => {
                      const isQualified = isQualifiedInvestmentLevel(level);
                      const pct = investmentModal.totalFullApps > 0 ? (count / investmentModal.totalFullApps) * 100 : 0;
                      return (
                        <div key={level} className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-medium truncate ${isQualified ? "text-green-500" : "text-muted-foreground"}`}>
                                {level} {isQualified ? "✓" : ""}
                              </span>
                              <span className="text-xs font-bold ml-2 shrink-0">{count} ({pct.toFixed(1)}%)</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted/30 overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{
                                width: `${pct}%`,
                                background: isQualified ? "var(--brand-gradient-start)" : "rgba(255,255,255,0.2)"
                              }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  <div className="border-t border-border pt-3 mt-3 flex justify-between text-xs">
                    <span className="text-muted-foreground">Qualified ($1k+)</span>
                    <span className="font-bold text-green-500">{investmentModal.qualifiedApps} / {investmentModal.totalFullApps} ({investmentModal.totalFullApps > 0 ? ((investmentModal.qualifiedApps / investmentModal.totalFullApps) * 100).toFixed(1) : 0}%)</span>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {stageLeadsModal && (
          <FunnelLeadsModal
            webinarId={stageLeadsModal.webinarId}
            webinarName={funnelCache[stageLeadsModal.webinarId]?.webinarName ?? stageLeadsModal.webinarId}
            stageLabel={stageLeadsModal.label}
            stageKey={stageLeadsModal.stageKey}
            traffic={stageLeadsModal.traffic}
            onClose={() => setStageLeadsModal(null)}
            onLeadClick={(email) => setTimelineEmail(email)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cancelledModal && (
          <CancelledLeadsModal
            webinarId={cancelledModal.webinarId}
            webinarName={funnelCache[cancelledModal.webinarId]?.webinarName ?? cancelledModal.webinarId}
            traffic={cancelledModal.traffic}
            onClose={() => setCancelledModal(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {timelineEmail && (
          <LeadTimelineDrawer
            email={timelineEmail}
            onClose={() => setTimelineEmail(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
